<template>
  <div class="home">

    <section class="home-knowledge-layout">
      <aside class="knowledge-sidebar">
        <div class="knowledge-brand-mark">
          <span class="brand-dot"></span>
          <div>
            <p>CAR DESIGN MAP</p>
            <strong>汽车设计知识库</strong>
          </div>
        </div>
        <nav class="knowledge-nav">
          <a href="#home-articles" class="knowledge-nav-link active" @click.prevent="scrollToSection('home-articles')">
            <span>首页</span>
            <em>{{ formatStatCount(articleTotalCount) }}</em>
          </a>
          <a href="#brand-section" class="knowledge-nav-link" @click.prevent="scrollToSection('brand-section')">
            <span>品牌索引</span>
            <em>{{ formatStatCount(brandTotalCount) }}</em>
          </a>
          <a href="#models-section" class="knowledge-nav-link" @click.prevent="scrollToSection('models-section')">
            <span>车型图谱</span>
            <em>{{ formatStatCount(modelTotalCount) }}</em>
          </a>
        </nav>
        <div class="knowledge-side-note">
          <span>更新中</span>
          <p>按品牌、年代、概念车线索组织汽车设计资料，适合快速浏览与灵感归档。</p>
        </div>
      </aside>

      <main ref="knowledgeMain" class="knowledge-main">
        <div class="knowledge-breadcrumb">
          <span>首页</span>
          <i>/</i>
          <span>设计资料库</span>
          <i>/</i>
          <span>最新上传</span>
        </div>

        <!-- 全屏轮播图展示区域 - 最新上传的车型 -->
        <div class="fullscreen-carousel knowledge-hero-card" v-if="carouselItems && carouselItems.length > 0">
          <!-- 轮播图容器 -->
          <div 
            class="carousel-container"
          >
            <div 
              class="carousel-slide" 
              v-for="(item, index) in carouselItems" 
              :key="item.type + '-' + item.id"
              :class="{ active: currentSlide === index }"
              :data-model-id="item.id"
            >
              <div class="carousel-slide-panel">
                <div class="knowledge-hero-visual">
                  <div class="slide-image-container">
                    <!-- 车型图片 -->
                    <img 
                      v-if="item.type === 'model' && getCarouselImageUrl(item)" 
                      :src="getCarouselImageUrl(item)"
                      :alt="item.name"
                      @load="handleModelImageLoad"
                      @error="handleModelImageError"
                      class="slide-image"
                      loading="eager"
                      decoding="async"
                      draggable="false"
                    >

                    <!-- 占位符 -->
                    <div class="slide-placeholder" :class="{ 
                      show: !getCarouselImageUrl(item) || 
                            modelImageLoadError[item.id] 
                    }">
                      <div class="placeholder-content">
                        <i class="el-icon-picture"></i>
                        <span>{{ $t('home.noImage') }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="knowledge-hero-content">
                  <div class="knowledge-hero-copy">
                    <div class="content-type-badge model-badge">{{ $t('home.latestUpload') }}</div>
                    <h1 class="slide-title">{{ item.name }}</h1>
                    <p class="slide-brand">{{ item.Brand ? item.Brand.name : $t('home.unknownBrand') }}</p>
                    <p class="knowledge-hero-summary">
                      从最新上传内容进入车型档案，继续查看高清图片、品牌归属与设计资料。
                    </p>
                    <button type="button" class="view-details-btn" @click.stop.prevent="$handleLinkClick($event, `/model/${item.id}`, { modelId: item.id })">
                      {{ $t('home.viewDetails') }}
                      <i class="el-icon-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 导航指示器 -->
          <div class="carousel-indicators">
            <div 
              v-for="(item, index) in carouselItems" 
              :key="item.type + '-' + item.id"
              class="indicator"
              :class="{ active: currentSlide === index }"
              @click="goToSlide(index)"
            ></div>
          </div>
          
          <!-- 左右导航按钮 -->
          <button class="carousel-nav prev" @click="prevSlide">
            <i class="el-icon-arrow-left"></i>
          </button>
          <button class="carousel-nav next" @click="nextSlide">
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
        
        <!-- 加载状态 -->
        <div v-else-if="latestModelsLoading" class="fullscreen-carousel knowledge-hero-card">
          <div class="loading-container">
            <div class="loading-spinner">
              <i class="el-icon-loading"></i>
              <p>{{ $t('home.loadingLatestModels') }}</p>
            </div>
          </div>
        </div>
        
        <!-- 错误状态 -->
        <div v-else-if="latestModelsError" class="fullscreen-carousel knowledge-hero-card">
          <div class="error-container">
            <div class="error-icon">
              <i class="el-icon-warning"></i>
            </div>
            <p class="error-text">{{ latestModelsError }}</p>
          </div>
        </div>

        <div class="knowledge-stat-strip">
          <div class="knowledge-stat-card">
            <span>已收录品牌</span>
            <strong>{{ formatStatCount(brandTotalCount) }}</strong>
          </div>
          <div class="knowledge-stat-card">
            <span>全部启用车型</span>
            <strong>{{ formatStatCount(modelTotalCount) }}</strong>
          </div>
          <div class="knowledge-stat-card">
            <span>已发布文章</span>
            <strong>{{ formatStatCount(articleTotalCount) }}</strong>
          </div>
        </div>

    <!-- 首页资讯区域 -->
    <section id="home-articles" class="home-articles-section">
      <div class="content-container">
        <div class="knowledge-section-heading">
          <div>
            <h2>最新资讯</h2>
            <p>把行业动态、设计观察和社区文章作为首页阅读流。</p>
          </div>
          <a href="/articles" @click.prevent="$handleLinkClick($event, '/articles')">查看全部</a>
        </div>

        <div class="home-articles-header">
          <div class="home-articles-title">
            <h2>最新资讯</h2>
            <p>行业动态与文章精选</p>
          </div>
        </div>

        <div v-if="homeArticlesLoading" class="home-articles-loading">
          <i class="el-icon-loading"></i>
          <span>正在加载资讯中，请稍等...</span>
        </div>
        <div v-else-if="homeArticlesError" class="home-articles-error">
          <i class="el-icon-warning-outline"></i>
          <span>{{ homeArticlesError }}</span>
        </div>
        <div v-else class="home-articles-content">
          <div class="home-articles-grid">
            <a
              v-for="article in homeAllArticles"
              :key="`article-${article.id}`"
              :href="getFullUrl(`/articles/${article.id}`)"
              class="home-article-card home-article-card-link"
              @click.prevent="$handleLinkClick($event, `/articles/${article.id}`)"
            >
              <div class="home-article-image">
                <img
                  :src="article.coverImage || '/default-article-cover.jpg'"
                  :alt="article.title"
                  loading="lazy"
                  decoding="async"
                >
                <span class="home-article-category">{{ article.category }}</span>
                <span v-if="article.isFeatured" class="home-article-badge">推荐</span>
              </div>
              <div class="home-article-body">
                <h3 class="home-article-title">{{ article.title }}</h3>
                <p class="home-article-summary">{{ article.summary }}</p>
                <div class="home-article-meta">
                  <span>{{ formatArticleDate(article.publishedAt) }}</span>
                  <span>{{ article.viewCount }} 阅读</span>
                </div>
              </div>
            </a>
          </div>

          <div v-if="homeAllArticles.length === 0" class="home-articles-empty">
            暂无资讯内容
          </div>
        </div>

        <div v-if="homeAllArticles.length > 0" class="home-articles-footer">
          <el-button
            type="text"
            class="home-articles-more"
            @click="$handleLinkClick($event, '/articles')"
          >
            查看全部
            <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
      </div>
    </section>
    
    <!-- 品牌导航区域 -->
    <div id="brand-section" class="brand-section">
      <div class="content-container">
        <div class="knowledge-section-heading compact">
          <div>
            <h2>品牌索引</h2>
            <p>以品牌与首字母为入口，快速进入车型档案。</p>
          </div>
        </div>
      </div>
      <!-- 品牌区域标题和折叠按钮 - 全宽背景 -->
      <div class="brand-section-header" @click="toggleBrandsDisplay">
        <div class="brand-section-title">
          <i class="el-icon-office-building"></i>
          <span>{{ $t('home.brandNav') }}</span>
          <span class="brand-count" v-if="!loading">({{ allBrands.length }})</span>
        </div>
        <div class="toggle-icon">
          <i :class="showBrands ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </div>
      </div>
      
      <div class="content-container">
      <!-- 品牌内容区域 - 可折叠 -->
      <div v-if="showBrands" class="brand-content-wrapper">
      <!-- 品牌分类选择器 -->
      <div class="brand-category-tabs">
        <button 
          :class="['category-tab', brandCategory === 'all' ? 'active' : '']"
          @click="setBrandCategory('all')"
        >
          {{ $t('home.allBrands') }}
        </button>
        <button 
          :class="['category-tab', brandCategory === 'domestic' ? 'active' : '']"
          @click="setBrandCategory('domestic')"
        >
          {{ $t('home.domesticBrands') }}
        </button>
        <button 
          :class="['category-tab', brandCategory === 'overseas' ? 'active' : '']"
          @click="setBrandCategory('overseas')"
        >
          {{ $t('home.overseasBrands') }}
        </button>
      </div>
      
      <!-- 字母筛选器 -->
      <div class="alphabet-filter">
        <button 
          :class="['alphabet-btn', currentLetter === '不限' ? 'active' : '']"
          @click="currentLetter = '不限'"
        >
          {{ $t('home.any') }}
        </button>
        <button 
          v-for="letter in 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')" 
          :key="letter"
          :class="['alphabet-btn', currentLetter === letter ? 'active' : '']"
          @click="currentLetter = letter"
        >
          {{ letter }}
        </button>
      </div>
      
      <!-- 品牌展示区域 -->
      <div class="brands-display">
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="3" animated />
        </div>
        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
        </div>
        <div v-else>
          <div v-if="filteredBrands.length === 0" class="no-brands-message">
            {{ $t('home.noBrandsFound') }}
          </div>
          <div v-else class="brands-grid">
            <a 
              class="brand-card brand-card-link"
              v-for="brand in filteredBrands" 
              :key="brand.id" 
              :href="getFullUrl(`/brand/${brand.id}`)"
              @click.prevent="$handleLinkClick($event, `/brand/${brand.id}`)"
              :data-brand-id="brand.id"
            >
              <div class="brand-logo">
                <img 
                  v-if="brand.logo" 
                  :src="brand.logo"
                  :alt="brand.name"
                  @load="handleLogoLoad"
                  @error="handleLogoError"
                  class="brand-logo-img"
                  loading="lazy"
                  decoding="async"
                >
                <div class="no-logo" :class="{ show: !brand.logo || logoLoadError[brand.id] }">
                  {{ brand.name.charAt(0) }}
                </div>
              </div>
              <div class="brand-name">{{ brand.name }}</div>
            </a>
          </div>
        </div>
        
        <!-- 品牌统计信息 -->
        <div class="brand-stats" v-if="!loading && filteredBrands.length > 0">
          <span class="stats-text">{{ $t('home.totalBrands', { count: filteredBrands.length }) }}</span>
          <span v-if="brandCategory !== 'all'" class="category-text">
            （{{ getCategoryName() }}）
          </span>
        </div>
      </div>
      </div>
      </div>
    </div>

    <!-- 车型展示区域 -->
    <div id="models-section" class="models-display-section">
      <div class="content-container">
      <div class="knowledge-section-heading compact">
        <div>
          <h2>车型图谱</h2>
          <p>按年代、概念车与上传时间筛选，形成可持续浏览的设计样本库。</p>
        </div>
      </div>
      
      <!-- 筛选控制栏 - 单行版 -->
      <div class="filter-control-bar">
        <!-- 年代筛选 -->
        <button 
          class="filter-btn" 
          :class="{ active: selectedDecade === '' }"
          @click="selectDecade('')"
        >
          {{ $t('home.all') }}
        </button>
        <button 
          v-for="decade in decades" 
          :key="decade.value"
          class="filter-btn" 
          :class="{ active: selectedDecade === decade.value }"
          @click="selectDecade(decade.value)"
        >
          {{ decade.label }}
        </button>
        
        <!-- 分隔符 -->
        <div class="filter-divider"></div>
        
        <!-- 概念车筛选 -->
        <button 
          class="filter-btn icon-btn" 
          :class="{ active: showConceptCars }"
          @click="toggleConceptCars"
        >
          <i class="el-icon-star-on"></i>
          {{ $t('home.conceptCars') }}
        </button>
        
        <!-- 排序控制 -->
        <button 
          class="filter-btn icon-btn" 
          :class="{ active: sortType === 'newest' }"
          @click="selectSortType('newest')"
        >
          <i class="el-icon-bottom"></i>
          {{ $t('home.newestFirst') }}
        </button>
        <button 
          class="filter-btn icon-btn" 
          :class="{ active: sortType === 'oldest' }"
          @click="selectSortType('oldest')"
        >
          <i class="el-icon-top"></i>
          {{ $t('home.oldestFirst') }}
        </button>
        <button 
          class="filter-btn icon-btn" 
          :class="{ active: sortType === 'latestUpload' }"
          @click="selectSortType('latestUpload')"
        >
          <i class="el-icon-upload2"></i>
          {{ $t('home.latestUploadSort') }}
        </button>
      </div>
      
      <div class="models-content">
        <div v-if="displayModelsLoading && displayModels.length === 0" class="loading-container">
          <div class="loading-spinner">
            <i class="el-icon-loading"></i>
            <p>{{ $t('home.loadingModels') }}</p>
          </div>
        </div>
        <div v-else-if="displayModelsError" class="error-container">
          <div class="error-icon">
            <i class="el-icon-warning"></i>
          </div>
          <p class="error-text">{{ displayModelsError }}</p>
          <button class="retry-btn" @click="retryFetchDisplayModels">
            <i class="el-icon-refresh"></i>
            {{ $t('home.retry') }}
          </button>
        </div>
        <div v-else-if="displayModels.length === 0" class="empty-container">
          <div class="empty-icon">
            <i class="el-icon-picture"></i>
          </div>
          <p class="empty-text">{{ $t('home.noModelsFound') }}</p>
        </div>
        <div v-else class="models-grid">
          <a
            class="model-display-card model-display-link" 
            v-for="(model, modelIndex) in displayModels" 
            :key="model.id"
            :href="`/model/${model.id}`"
            :data-model-id="model.id"
            @click.prevent="goToModelDetail(model.id)"
          >
            <div class="model-display-image">
              <img 
                v-if="getModelImageUrl(model)" 
                :src="getModelImageUrl(model)"
                :alt="model.name"
                @load="handleModelImageLoad"
                @error="handleModelImageError"
                class="model-display-img"
                :loading="modelIndex < 8 ? 'eager' : 'lazy'"
                :fetchpriority="modelIndex < 4 ? 'high' : 'auto'"
                decoding="async"
                draggable="false"
              >
              <div class="model-display-placeholder" :class="{ show: !getModelImageUrl(model) || modelImageLoadError[model.id] }">
                <div class="placeholder-content">
                  <i class="el-icon-picture"></i>
                  <span>{{ $t('home.noImage') }}</span>
                </div>
                <!-- 图片加载失败时显示重试按钮 -->
                <button 
                  v-if="modelImageLoadError[model.id]" 
                  class="image-retry-btn" 
                  @click="retryImageLoad(model)"
                >
                  <i class="el-icon-refresh"></i>
                  {{ $t('home.retry') }}
                </button>
              </div>
              <!-- 加载中的骨架屏 -->
              <div class="model-image-skeleton" v-if="model.Images && model.Images.length > 0">
                <div class="skeleton-shimmer"></div>
              </div>
            </div>
            <div class="model-display-info">
              <h3 class="model-display-name">{{ model.name || $t('home.modelName') }}</h3>
            </div>
          </a>
        </div>
        
        <!-- 加载状态指示器 -->
        <div v-if="displayModelsLoading" class="loading-indicator">
          <i class="el-icon-loading"></i>
          <span>{{ $t('home.loadingMoreModels') }}</span>
        </div>
        
        <!-- 没有更多数据提示 -->
        <div v-else-if="!hasMoreDisplayModels && displayModels.length > 0" class="no-more-data">
          <span>{{ $t('home.allModelsLoaded') }}</span>
        </div>
      </div>
      </div>
    </div>

      </main>

      <aside class="knowledge-reference-panel">
        <div class="reference-block">
          <p class="reference-label">最新资讯</p>
          <a
            v-for="article in homeAllArticles.slice(0, 2)"
            :key="`ref-article-${article.id}`"
            :href="getFullUrl(`/articles/${article.id}`)"
            class="reference-link"
            @click.prevent="$handleLinkClick($event, `/articles/${article.id}`)"
          >
            {{ article.title }}
          </a>
          <span v-if="homeAllArticles.length === 0" class="reference-empty">暂无资讯内容</span>
        </div>
        <div class="reference-block">
          <p class="reference-label">快速入口</p>
          <a href="/articles" class="reference-link" @click.prevent="$handleLinkClick($event, '/articles')">行业资讯与文章精选</a>
          <a href="/image-gallery" class="reference-link" @click.prevent="$handleLinkClick($event, '/image-gallery')">标签搜索图片库</a>
          <a href="/inspiration" class="reference-link" @click.prevent="$handleLinkClick($event, '/inspiration')">灵感收藏与浏览</a>
        </div>
      </aside>

      <footer class="knowledge-site-footer">
        <p>© 2025 CARDESIGNSPACE</p>
      </footer>
    </section>
    
    <!-- 悬浮反馈组件 -->
    <FloatingFeedback @feedback-submitted="handleFeedbackSubmitted" />
  </div>
</template>

<script>
import { brandAPI, modelAPI, imageAPI, apiClient } from '@/services/api';
import { applyCosOptimization, isSignedCosUrl } from '@/utils/assetUrl';
import FloatingFeedback from '@/components/FloatingFeedback.vue';
// 恢复使用chinese-to-pinyin库
import chineseToPinyin from 'chinese-to-pinyin'
import scrollPositionMixin from '@/utils/scrollPositionMixin';
import scrollPositionManager from '@/utils/scrollPositionManager';
import { getFullUrl } from '@/utils/routerUtils';
import axios from 'axios';

export default {
  name: 'Home',
  components: {
    FloatingFeedback
  },
  mixins: [scrollPositionMixin],
  data() {
    return {
      carouselItems: [
        {
          title: this.$t('home.latestUploadedModels'),
          description: this.$t('home.discoverHighDef'),
          imageUrl: 'https://via.placeholder.com/1920x1080?text=Chinese+Brands'
        },
        {
          title: this.$t('home.professionalGallery'),
          description: this.$t('home.discoverTenThousand'),
          imageUrl: 'https://via.placeholder.com/1920x1080?text=Car+Image+2'
        },
        {
          title: this.$t('home.panoramicView'),
          description: this.$t('home.experienceDetails'),
          imageUrl: 'https://via.placeholder.com/1920x1080?text=Car+Image+3'
        }
      ],
      chineseBrands: [],
      allBrands: [],
      brandTotalCount: null,
      modelTotalCount: null,
      articleTotalCount: null,
      loading: true,
      modelLoading: true,
      error: null,
      latestModels: [],
      carouselItems: [], // 轮播项目（最新上传的车型）
      allModelsData: [], // 存储所有车型数据
      currentPage: 1,
      pageSize: 24,
      hasMoreModels: true,
      loadingMore: false,
      letters: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
      currentLetter: '不限',
      onlyShowingSelling: false,
      originFilter: 'china', // 默认显示中国品牌
      specialBrandMapping: {
        // 中国品牌
        '蔚来': 'W', // 确保"蔚来"归到W类
        '哪吒': 'N', // 确保"哪吒"归到N类
        '威马': 'W', // 确保"威马"归到W类
        '理想': 'L', // 确保"理想"归到L类
        '小鹏': 'X', // 确保"小鹏"归到X类
        '极氪': 'J', // 确保"极氪"归到J类
        '几何': 'J', // 确保"几何"归到J类
        '深蓝': 'S', // 确保"深蓝"归到S类
        '长安': 'C', // 修复"长安"的首字母
        '长城': 'C', // 修复"长城"的首字母
        '长': 'C',   // 修复所有"长"字开头的品牌
        '奇瑞': 'Q', // 确保"奇瑞"归到Q类
        '广汽': 'G', // 确保"广汽"归到G类
        '吉利': 'J',  // 确保"吉利"归到J类
        '奥迪': 'A',
        '埃安': 'A',
        '欧拉': 'O',
        '阿维塔': 'A',
        '五菱': 'W',  // 确保"五菱"归到W类
        
        // 合资品牌（现归类为国内品牌）
        '一汽-大众': 'Y',
        '上汽大众': 'S',
        '一汽丰田': 'Y',
        '广汽丰田': 'G',
        '东风日产': 'D',
        '广汽本田': 'G',
        '东风本田': 'D',
        '北京现代': 'B',
        '东风悦达起亚': 'D',
        '长安福特': 'C',
        '上汽通用': 'S',
        '华晨宝马': 'H',
        '北京奔驰': 'B',
        '一汽奥迪': 'Y',
        
        // 海外品牌首字母映射
        // 英文品牌直接使用首字母
        'BMW': 'B',
        'MINI': 'M',
        'Jeep': 'J',
        
        // 其他可能需要特殊处理的品牌可以在这里添加
      },
      brandCategory: 'all', // 新增：品牌分类
      logoLoadError: {},
      latestModelsLoading: true,
      latestModelsError: null,
      modelImageLoadError: {},
      showBrands: false, // 默认折叠，降低首屏 DOM 和图片解码压力

      // 首页资讯
      homeFeaturedArticles: [],
      homeArticles: [],
      homeArticlesLoading: false,
      homeArticlesError: null,
      
      // 轮播图相关数据
      currentSlide: 0,
      autoPlayInterval: null,
      
      // 车型展示相关数据
      displayModels: [],
      displayModelsLoading: false,
      displayModelsError: null,
      sortType: 'latestUpload', // 'newest' 为新车优先，'oldest' 为老车优先，'latestUpload' 为最新上传
      currentDisplayPage: 1,
      displayPageSize: 12, // 首页首批少渲染，滚动到底部再增量加载
      hasMoreDisplayModels: true,
      
      // 年代筛选相关
      selectedDecade: '', // 当前选中的年代
      decades: [
        { label: '2020s', value: '2020s' },
        { label: '2010s', value: '2010s' },
        { label: '2000s', value: '2000s' },
        { label: '90s', value: '1990s' },
        { label: '80s', value: '1980s' },
        { label: '70s', value: '1970s' },
        { label: '60s', value: '1960s' },
        { label: '50s', value: '1950s' },
        { label: '40s', value: '1940s' },
        { label: '30s', value: '1930s' },
        { label: '20s', value: '1920s' },
        { label: '10s', value: '1910s' },
        { label: '00s', value: '1900s' }
      ],
      
      // 概念车筛选相关
      showConceptCars: false, // 是否显示概念车

      // 图片变体缓存，避免重复请求
      imageVariantCache: {},
      pendingVariantRequests: new Set(),
      
      // 数据缓存
      dataCache: {
        brands: null,
        brandsCacheTime: null,
        carousel: null,
        carouselCacheTime: null,
        displayModels: new Map(), // 按页码缓存车型数据
        displayModelsCacheTime: new Map()
      },
      
      // 错误重试机制
      errorRetryCount: 0,
      maxRetryCount: 3,
      retryDelay: 2000, // 2秒后重试
      
      // 请求限制
      requestQueue: [],
      maxConcurrentRequests: 1, // 减少到1个并发请求，避免阻塞
      activeRequests: 0,
      
      // 防重复加载机制
      lastLoadTime: 0,
      loadCooldown: 2000, // 2秒冷却时间
      
      // 初始化状态管理
      isInitializing: true,
      
      // 组件状态管理
      isComponentActive: false,
      
      // 图片变体请求管理
      variantRequestQueue: [],
      maxVariantRequests: 1, // 限制变体请求并发数
      activeVariantRequests: 0,
      variantRequestDelay: 2000, // 变体请求延迟2秒

      // 滚动性能控制
      scrollRafId: null,
      scrollListenerTarget: null
    }
  },
  computed: {
    // 仅保留中国品牌
    chineseOnlyBrands() {
      return this.allBrands.filter(brand => brand.country === '中国');
    },

    // 首页资讯：推荐优先，去重合并
    homeAllArticles() {
      const featured = (this.homeFeaturedArticles || []).map(article => ({
        ...article,
        isFeatured: true
      }));
      const featuredIds = new Set(featured.map(article => article.id));
      const latest = (this.homeArticles || [])
        .filter(article => !featuredIds.has(article.id))
        .map(article => ({
          ...article,
          isFeatured: false
        }));
      return [...featured, ...latest];
    },
    
    // 品牌按拼音首字母分组
    brandsByLetter() {
      const result = {};
      
      // 初始化所有字母的空数组
      this.letters.forEach(letter => {
        result[letter] = [];
      });
      
      // 将中国品牌根据拼音首字母进行分组
      this.chineseOnlyBrands.forEach(brand => {
        const firstLetter = this.getFirstLetter(brand.name);
        if (result[firstLetter]) {
          result[firstLetter].push(brand);
        } else {
          // 如果不在A-Z内，归类到其他类别
          if (!result['#']) {
            result['#'] = [];
          }
          result['#'].push(brand);
        }
      });
      
      return result;
    },
    // 可用的字母筛选（有对应品牌的字母）
    availableLetters() {
      return this.letters.filter(letter => {
        return this.brandsByLetter[letter] && this.brandsByLetter[letter].length > 0;
      });
    },
    // 根据筛选条件过滤品牌
    filteredBrands() {
      let brands = [];
      
      // 首先根据品牌分类筛选
      if (this.brandCategory === 'all') {
        brands = this.allBrands.slice();
      } else if (this.brandCategory === 'domestic') {
        // 国内品牌（包括自主品牌和合资品牌）
        brands = this.allBrands.filter(brand => 
          brand.country === '中国' || 
          brand.country === '合资' || 
          (brand.country && brand.country.includes('合资'))
        );
      } else if (this.brandCategory === 'overseas') {
        // 海外品牌（排除中国和合资品牌）
        brands = this.allBrands.filter(brand => 
          brand.country && 
          brand.country !== '中国' && 
          brand.country !== '合资' && 
          !brand.country.includes('合资')
        );
      }
      
      // 然后根据字母筛选
      if (this.currentLetter !== '不限') {
        brands = brands.filter(brand => {
          const firstLetter = this.getFirstLetter(brand.name);
          return firstLetter === this.currentLetter;
        });
      }

      // 过滤掉脏数据，避免接口异常或缓存异常时导致渲染崩溃
      brands = brands.filter((brand) => brand && typeof brand.name === 'string' && brand.name.trim());
      
      // 根据拼音首字母和品牌名排序
      return brands.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';

        // 首先按照首字母排序
        const letterA = this.getFirstLetter(nameA);
        const letterB = this.getFirstLetter(nameB);
        
        if (letterA !== letterB) {
          return letterA.localeCompare(letterB);
        }
        
        // 如果首字母相同，按照品牌名称排序
        return nameA.localeCompare(nameB, 'zh-CN');
      });
    }
  },
  methods: {
    formatStatCount(value) {
      if (value === null || value === undefined || value === '') {
        return '--';
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue.toLocaleString('zh-CN') : '--';
    },

    runWhenIdle(callback, timeout = 800) {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout });
      } else {
        setTimeout(callback, 120);
      }
    },

    // 用于资讯卡片 <a href> 的完整 URL，供浏览器原生右键菜单使用
    getFullUrl(path) {
      return getFullUrl(path);
    },

    // 切换品牌展示区域的显示/隐藏
    toggleBrandsDisplay() {
      this.showBrands = !this.showBrands;
    },
    
    // 获取车型图片URL的辅助方法 - ⭐ 优化为优先使用缩略图
    // ⭐ 获取轮播图高质量图片URL（不压缩，最高清）
    getCarouselImageUrl(model) {
      // 防御性检查，确保model是对象
      if (!model || typeof model !== 'object') {
        console.error('无效的模型数据:', model);
        return '/images/default-car.jpg';
      }
      
      // ⭐ 轮播图优先使用高质量变体或原图
      if (model.Images && Array.isArray(model.Images) && model.Images.length > 0) {
        const image = model.Images[0];
        // 轮播大图优先：Assets.large / Assets.medium / bestUrl / 原图
        if (image.Assets && Array.isArray(image.Assets)) {
          // 优先级：large > medium > webp > small > thumb
          const large = image.Assets.find(a => a.variant === 'large');
          if (large && large.url) {
            return large.url;
          }
          const medium = image.Assets.find(a => a.variant === 'medium');
          if (medium && medium.url) {
            return medium.url;
          }
          const webp = image.Assets.find(a => a.variant === 'webp');
          if (webp && webp.url) {
            return webp.url;
          }
          const small = image.Assets.find(a => a.variant === 'small');
          if (small && small.url) {
            return small.url;
          }
          const thumb = image.Assets.find(a => a.variant === 'thumb');
          if (thumb && thumb.url) {
            return thumb.url;
          }
        }
        // 没有 Assets 时，退回高质量字段和原图
        if (image.bestUrl) {
          return image.bestUrl;
        }
        if (image.url) {
          return image.url;
        }
        // 最后再考虑优化版/展示版（防止没有大图时完全没图）
        if (image.optimizedUrl) {
          return image.optimizedUrl;
        }
        if (image.displayUrl) {
          return image.displayUrl;
        }
      }
      
      // 尝试使用模型自身的coverUrl（封面图）
      if (model.coverUrl && typeof model.coverUrl === 'string' && model.coverUrl.trim() !== '') {
        return model.coverUrl;
      }
      
      // 如果找不到任何图片，返回默认图片
      return '/images/default-car.jpg';
    },
    
    // 获取车型卡片图片URL（优化尺寸，平衡质量和性能）
    getModelImageUrl(model) {
      // 防御性检查，确保model是对象
      if (!model || typeof model !== 'object') {
        console.error('无效的模型数据:', model);
        return '/images/default-car.jpg';
      }
      
      // 卡片只需要中等缩略图，避免车型图谱新增图片解码过慢。
      const CARD_IMAGE_WIDTH = 360;
      const CARD_IMAGE_HEIGHT = 225;
      
      // ⭐ 1. 首先尝试使用缩略图（最小尺寸，加载最快）
      if (model.Images && Array.isArray(model.Images) && model.Images.length > 0) {
        const image = model.Images[0];
        if (image.optimizedUrl) {
          return image.optimizedUrl;
        }
        if (image.displayUrl) {
          return image.displayUrl;
        }
        if (image.bestUrl) {
          return image.bestUrl;
        }
        // 尝试从 Assets 中获取合适的卡片变体，优先小图以降低带宽
        if (image.Assets && Array.isArray(image.Assets)) {
          // ⭐ 优先级：thumb/thumbnail > small > webp > medium
          const thumbnail = image.Assets.find(a => a.variant === 'thumbnail' || a.variant === 'thumb');
          if (thumbnail && thumbnail.url) {
            return thumbnail.url;
          }
          const small = image.Assets.find(a => a.variant === 'small');
          if (small && small.url) {
            return small.url;
          }
          const webp = image.Assets.find(a => a.variant === 'webp');
          if (webp && webp.url) {
            return webp.url;
          }
          const medium = image.Assets.find(a => a.variant === 'medium');
          if (medium && medium.url) {
            return medium.url;
          }
        }
        // 如果没有Assets，尝试thumbnailUrl
        if (image.thumbnailUrl) {
          return image.thumbnailUrl;
        }
        // ⭐ 回退到原图时，使用buildFallbackImageUrl限制尺寸，但保持较高质量
        if (image.url) {
          return this.buildFallbackImageUrl(image.url, CARD_IMAGE_WIDTH, CARD_IMAGE_HEIGHT, true);
        }
      }
      
      // 2. 其次尝试使用模型自身的thumbnail属性
      if (model.thumbnail && typeof model.thumbnail === 'string' && model.thumbnail.trim() !== '') {
        return model.thumbnail;
      }
      
      // 3. 尝试使用模型自身的coverUrl（封面图），也限制尺寸但保持质量
      if (model.coverUrl && typeof model.coverUrl === 'string' && model.coverUrl.trim() !== '') {
        return this.buildFallbackImageUrl(model.coverUrl, CARD_IMAGE_WIDTH, CARD_IMAGE_HEIGHT, true);
      }
      
      // 4. 如果找不到任何图片，返回默认图片
      return '/images/default-car.jpg';
    },
    // 获取品牌名称拼音首字母
    getFirstLetter(name) {
      if (!name) return '#';
      
      // 1. 首先检查特殊品牌映射表
      if (this.specialBrandMapping[name]) {
        return this.specialBrandMapping[name];
      }
      
      // 2. 检查常见品牌前缀（2-3字品牌名前缀）
      if (name.length >= 2) {
        const prefix2 = name.substring(0, 2);
        if (this.specialBrandMapping[prefix2]) {
          return this.specialBrandMapping[prefix2];
        }
        
        if (name.length >= 3) {
          const prefix3 = name.substring(0, 3);
          if (this.specialBrandMapping[prefix3]) {
            return this.specialBrandMapping[prefix3];
          }
        }
      }
      
      // 3. 使用chinese-to-pinyin库获取拼音
      try {
        // 获取第一个汉字的拼音
        const firstChar = name.charAt(0);
        const pinyinStr = chineseToPinyin(firstChar, { removeSpace: true, firstCharacter: true });
        if (pinyinStr && pinyinStr.length > 0) {
          return pinyinStr.toUpperCase();
        }
      } catch (error) {
        console.error('获取拼音出错:', error);
      }
      
      // 4. 处理英文品牌
      const firstChar = name.charAt(0);
      const firstCharCode = firstChar.charCodeAt(0);
      
      // 如果是字母，直接返回大写
      if ((firstCharCode >= 65 && firstCharCode <= 90) || (firstCharCode >= 97 && firstCharCode <= 122)) {
        return firstChar.toUpperCase();
      }
      
      // 5. 如果以上方法都无法确定，返回#
      return '#';
    },
    // 从车型名称中提取年份
    extractYearFromName(name) {
      if (!name) return null;
      
      // 匹配4位数字年份（1900-2099范围内，支持更广泛的年份范围）
      const yearMatch = name.match(/\b(19|20)\d{2}\b/g);
      if (yearMatch && yearMatch.length > 0) {
        // 如果有多个年份，返回最大的（最新的）
        const years = yearMatch.map(year => parseInt(year)).filter(year => year >= 1900 && year <= 2099);
        if (years.length > 0) {
          return Math.max(...years);
        }
      }
      
      return null;
    },
    // 获取车型的最终年份（优先使用名称中的年份，其次使用数据库年份）
    getModelYear(model) {
      // 1. 优先从车型名称中提取年份
      const nameYear = this.extractYearFromName(model.name);
      if (nameYear) {
        return nameYear;
      }
      
      // 2. 使用数据库中的年份
      if (model.year && model.year > 0) {
        return model.year;
      }
      
      // 3. 默认返回一个较小的年份，确保排在后面
      return 1900;
    },
    // 导航到品牌详情页
    goToBrand(brandId) {
      this.$router.push(`/brand/${brandId}`);
    },
    // 导航到车型详情页
    goToModel(modelId) {
      // 保存当前滚动位置和车型ID
      // ⭐ 禁用滚动位置保存，提升性能
      // 不再保存滚动位置
      // const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
      // scrollPositionManager.saveModelPosition(this.$route.path, modelId, currentPosition);
      // this.saveScrollPosition();
      
      this.$router.push(`/model/${modelId}`);
    },
    
    // 跳转到车型详情页 - 用于<a>标签的点击处理
    goToModelDetail(modelId) {
      this.goToModel(modelId);
    },

    // 调试方法：手动测试滚动位置功能
    debugScrollPosition() {
      const route = this.$route.path;
      const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
      const savedPosition = scrollPositionManager.getPosition(route);
      
      console.log('=== 滚动位置调试信息 ===');
      console.log(`当前路由: ${route}`);
      console.log(`当前滚动位置: ${currentPosition}px`);
      console.log(`保存的滚动位置: ${savedPosition}px`);
      console.log(`页面高度: ${document.documentElement.scrollHeight}px`);
      console.log(`窗口高度: ${window.innerHeight}px`);
      console.log(`所有保存的位置:`, scrollPositionManager.positions);
      console.log('========================');
    },

    // ⭐ 禁用滚动位置恢复功能，提升页面加载性能
    // ⭐ 禁用滚动位置恢复功能，提升页面加载性能
    async preloadDataForScrollRestore() {
      // 不再执行滚动位置恢复相关的预加载
      return;
    },

    // 根据车型ID恢复位置
    async restoreToModelPosition(modelId, fallbackPosition) {
      console.log(`🔍 开始查找车型 ${modelId} 的位置...`);
      
      const findModelElement = () => {
        // 查找车型卡片元素
        const modelCard = document.querySelector(`[data-model-id="${modelId}"]`);
        if (modelCard) {
          const rect = modelCard.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + scrollTop;
          
          console.log(`✅ 找到车型 ${modelId}，位置: ${elementTop}px`);
          return elementTop;
        }
        return null;
      };

      // 尝试查找车型元素
      let modelPosition = findModelElement();
      
      if (modelPosition === null) {
        console.log(`⚠️ 未找到车型 ${modelId}，等待数据加载...`);
        
        // 如果没找到，等待数据加载后再试
        let attempts = 0;
        const maxAttempts = 10;
        
        while (modelPosition === null && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 200));
          modelPosition = findModelElement();
          attempts++;
          
          if (modelPosition === null) {
            console.log(`⏳ 等待车型 ${modelId} 加载... (尝试 ${attempts}/${maxAttempts})`);
          }
        }
      }

      // 确定最终滚动位置
      const finalPosition = modelPosition !== null ? modelPosition : fallbackPosition;
      
      if (modelPosition !== null) {
        console.log(`🎯 滚动到车型 ${modelId} 的位置: ${finalPosition}px`);
      } else {
        console.log(`⚠️ 未找到车型 ${modelId}，使用备用位置: ${finalPosition}px`);
      }

      // 滚动到目标位置
      this.scrollToPosition(finalPosition);
      
      // 高亮显示目标车型（可选）
      if (modelPosition !== null) {
        this.highlightModel(modelId);
      }
    },

    // 高亮显示车型
    highlightModel(modelId) {
      const modelCard = document.querySelector(`[data-model-id="${modelId}"]`);
      if (modelCard) {
        // 添加高亮效果
        modelCard.style.transition = 'all 0.3s ease';
        modelCard.style.transform = 'scale(1.05)';
        modelCard.style.boxShadow = '0 0 20px rgba(255, 193, 7, 0.5)';
        
        // 3秒后移除高亮效果
        setTimeout(() => {
          modelCard.style.transform = '';
          modelCard.style.boxShadow = '';
        }, 3000);
      }
    },

    normalizeBrandRecords(brands) {
      if (!Array.isArray(brands)) return [];

      return brands
        .map((brand) => {
          if (!brand || typeof brand !== 'object') return null;
          if (brand.dataValues && typeof brand.dataValues === 'object') {
            return { ...brand.dataValues };
          }
          return { ...brand };
        })
        .filter((brand) => brand && typeof brand.name === 'string' && brand.name.trim());
    },

    // 获取品牌列表 - 优化缓存机制
    async fetchChineseBrands() {
      this.loading = true;
      this.error = null;
      this.resetLogoLoadState();
      
      try {
        // 检查内存缓存（5分钟有效期）
        const now = Date.now();
        const cacheValidTime = 5 * 60 * 1000; // 5分钟
        
        if (this.dataCache.brands && this.dataCache.brandsCacheTime && 
            (now - this.dataCache.brandsCacheTime) < cacheValidTime) {
          console.log('使用内存缓存的品牌数据');
          this.allBrands = this.normalizeBrandRecords(this.dataCache.brands);
          this.brandTotalCount = this.allBrands.length;
          this.loading = false;
          return;
        }
        
        // 检查本地存储缓存（10分钟有效期）
        // 升级 key：避免沿用旧缓存里的直连 COS 链接（生产 Referer 防盗链下会整站破图）
        const cacheKey = 'brands_cache_v2';
        const cacheTimeKey = 'brands_cache_time_v2';
        try {
          localStorage.removeItem('brands_cache');
          localStorage.removeItem('brands_cache_time');
        } catch (_) {
          /* ignore */
        }
        const cachedData = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);
        const localCacheValidTime = 10 * 60 * 1000; // 10分钟
        
        if (cachedData && cacheTime && (now - parseInt(cacheTime)) < localCacheValidTime) {
          console.log('使用本地缓存的品牌数据');
          this.allBrands = this.normalizeBrandRecords(JSON.parse(cachedData));
          this.brandTotalCount = this.allBrands.length;
          // 更新内存缓存
          this.dataCache.brands = this.allBrands;
          this.dataCache.brandsCacheTime = now;
          this.loading = false;
          return;
        }
        
        // 从API获取品牌列表
        const response = await brandAPI.getAll();
        console.log('API返回的原始品牌数据:', response);
        
        // 适配/upload/brands接口的返回格式 { status: 'success', data: [...] }
        if (response.status === 'success' && Array.isArray(response.data)) {
          this.allBrands = this.normalizeBrandRecords(response.data);
          const responseCount = Number(response.count);
          this.brandTotalCount = Number.isFinite(responseCount) ? responseCount : this.allBrands.length;
        } else if (response.data && Array.isArray(response.data)) {
          // 兼容原有格式 { success: true, data: [...] }
          this.allBrands = this.normalizeBrandRecords(response.data);
          const responseCount = Number(response.count);
          this.brandTotalCount = Number.isFinite(responseCount) ? responseCount : this.allBrands.length;
        } else {
          // 如果response.data不存在或不是数组，使用整个response作为数据
          this.allBrands = this.normalizeBrandRecords(Array.isArray(response) ? response : []);
          this.brandTotalCount = this.allBrands.length;
        }
        
        // 缓存数据到内存和本地存储
        if (this.allBrands && this.allBrands.length > 0) {
          // 更新内存缓存
          this.dataCache.brands = this.allBrands;
          this.dataCache.brandsCacheTime = now;
          
          // 更新本地存储缓存
          localStorage.setItem(cacheKey, JSON.stringify(this.allBrands));
          localStorage.setItem(cacheTimeKey, now.toString());
        }
        
        // 如果没有数据，显示提示信息
        if (!this.allBrands || this.allBrands.length === 0) {
          this.error = '数据库中未找到品牌数据';
        }
        
        // 强制刷新筛选状态
        this.currentLetter = '不限';
        this.brandCategory = 'all'; // 重置分类
        
      } catch (error) {
        console.error('获取品牌列表失败:', error);
        if (error.response) {
          // 服务器响应了错误状态码
          this.error = `连接数据库失败 (${error.response.status}): ${error.response.data.message || '请检查网络连接或联系管理员'}`;
        } else if (error.request) {
          // 请求发送了但没有收到响应
          this.error = '无法连接到服务器，请检查后端服务是否正在运行';
        } else {
          // 请求设置时发生错误
          this.error = '连接数据库失败，请检查网络连接或联系管理员';
        }
      } finally {
        this.loading = false;
      }
    },
    // 加载更多车型
    async loadMoreModels() {
      this.loadingMore = true;
      try {
        const nextPage = this.currentPage + 1;
        console.log(`加载第 ${nextPage} 页车型`);
        
        // 调用API获取下一页数据
        const response = await modelAPI.getAll({
          latest: true,
          page: nextPage,
          limit: this.pageSize,
          sortOrder: 'desc' // 确保按年份降序排列，最新车型在前
        });
        
        if (response.success && Array.isArray(response.data)) {
          // 为新加载的车型设置缩略图
          response.data.forEach(model => {
            if (!model.thumbnail && model.Images && model.Images.length > 0) {
              const firstImage = model.Images[0];
              model.thumbnail = firstImage.url;
            }
          });
          
          // 将新数据添加到现有列表
          this.latestModels = [...this.latestModels, ...response.data];
          this.currentPage = response.page;
          this.hasMoreModels = response.page < response.totalPages;
          
          console.log(`成功加载第 ${nextPage} 页，新增 ${response.data.length} 个车型`);
        } else {
          console.error('加载更多车型失败：无效的响应数据');
          this.hasMoreModels = false;
        }
      } catch (error) {
        console.error('加载更多车型失败:', error);
        this.$message.error('加载更多车型失败，请稍后重试');
      } finally {
        this.loadingMore = false;
        
        // 重新初始化懒加载
        this.$nextTick(() => {
          this.initLazyLoading();
        });
      }
    },
    // 初始化图片懒加载
    initLazyLoading() {
      if (this.lazyLoadObserver) {
        this.lazyLoadObserver.disconnect();
        this.lazyLoadObserver = null;
      }
    },
    
    // 观察懒加载图片
    observeLazyImages() {
      // 首页车型图片改用浏览器原生 loading="lazy"。
      // 这里保留空方法，避免数据加载流程中已有调用触发额外 JS 图片队列。
    },
    
    // 优化图片预加载 - 减少预加载数量
    preloadNextBatchImages() {
      // 首页滚动路径上不再做 JS 预加载。浏览器原生 lazy loading 更稳定，
      // 避免用户停留片刻后首次滚动撞上延迟图片请求和解码任务。
    },
    
    // 根据上下文返回最佳图片URL（优先使用变体）
    getOptimizedImageUrl(imageInput, width = 400, height = 300, context = 'card') {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        // 如果请求原图，直接返回原图URL，不添加压缩参数
        if (context === 'original') {
          return imageInput?.url || '';
        }
        // ⭐ 卡片场景使用高质量
        return this.buildFallbackImageUrl(imageInput?.url || '', width, height, context === 'card');
      }
      
      const { imageId, url, image } = this.normalizeImageInput(imageInput);
      if (!url) return '';

      // 如果请求原图，直接返回原图URL，不经过变体系统
      if (context === 'original') {
        // 签名 URL 不能裁掉查询参数，否则会直接失效。
        if (isSignedCosUrl(url)) {
          return url;
        }
        // 移除 URL 中的压缩参数（如果有）
        const cleanUrl = url.split('?')[0].split('&')[0];
        return cleanUrl;
      }

      // 移除路由检查，允许图片变体请求功能
      // if (this.$route.path !== '/') {
      //   return this.buildFallbackImageUrl(url, width, height);
      // }

      if (image && image.optimizedUrl) {
        return image.optimizedUrl;
      }

      const cacheKey = imageId ? this.buildVariantCacheKey(imageId, context, width, height) : null;
      if (cacheKey && this.imageVariantCache[cacheKey]) {
        const cachedUrl = this.imageVariantCache[cacheKey];
        if (image && image.optimizedUrl !== cachedUrl) {
          this.$set(image, 'optimizedUrl', cachedUrl);
          this.$set(image, 'displayUrl', cachedUrl);
        }
        return cachedUrl;
      }

      // 使用队列管理变体请求，避免阻塞主要数据加载
      if (imageId && cacheKey && !this.pendingVariantRequests.has(cacheKey)) {
        this.pendingVariantRequests.add(cacheKey);
        this.queueVariantRequest({
          imageId,
          cacheKey,
          width,
          height,
          context,
          fallbackUrl: url,
          imageRef: image
        });
      }

      // ⭐ 卡片场景使用高质量，确保图片清晰
      return this.buildFallbackImageUrl(url, width, height, context === 'card');
    },
    
    // 队列管理变体请求
    queueVariantRequest(requestData) {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        return;
      }
      
      // 创建取消令牌
      const cancelToken = axios.CancelToken.source();
      requestData.cancelToken = cancelToken;
      
      // 添加到队列
      this.variantRequestQueue.push(requestData);
      
      // 延迟处理变体请求，避免阻塞主要数据加载
      setTimeout(() => {
        this.processVariantQueue();
      }, this.variantRequestDelay);
    },
    
    // 处理变体请求队列
    async processVariantQueue() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        this.variantRequestQueue = [];
        return;
      }
      
      // 检查并发限制
      if (this.activeVariantRequests >= this.maxVariantRequests) {
        return;
      }
      
      // 处理队列中的请求
      if (this.variantRequestQueue.length > 0) {
        const requestData = this.variantRequestQueue.shift();
        this.activeVariantRequests++;
        
        try {
          await this.fetchVariantUrl(requestData);
        } finally {
          this.activeVariantRequests--;
          // 继续处理队列 - 使用 requestIdleCallback 优化
          if (this.variantRequestQueue.length > 0) {
            if ('requestIdleCallback' in window) {
              requestIdleCallback(() => {
                this.processVariantQueue();
              }, { timeout: 100 });
            } else {
              setTimeout(() => {
                this.processVariantQueue();
              }, 50); // 减少间隔到50ms，提升响应速度
            }
          }
        }
      }
    },
    
    // 根据使用场景获取合适的变体类型
    getVariantForContext(context) {
      switch (context) {
        case 'thumbnail':
          return 'thumb';
        case 'card':
          return 'small';
        case 'detail':
          return 'medium';
        case 'fullscreen':
          return 'large';
        case 'original':
          return 'original'; // 原图
        default:
          return 'webp';
      }
    },

    buildVariantCacheKey(imageId, context, width, height) {
      return `${imageId}|${context}|${width || ''}|${height || ''}`;
    },

    buildFallbackImageUrl(url, width, height, highQuality = false) {
      if (!url) return '';

      const safeWidth = width || 600;
      const safeHeight = height || 400;
      
      // ⭐ 根据用途设置质量：卡片使用90%（更清晰），其他使用80%
      // highQuality为true时使用90%质量，确保卡片图片非常清晰
      const quality = highQuality ? 90 : (width <= 300 ? 80 : 85);

      const optimizedUrl = applyCosOptimization(url, safeWidth, safeHeight, quality);
      if (optimizedUrl !== url) return optimizedUrl;

      if (url.includes('/api/') || url.startsWith('/')) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}w=${safeWidth}&h=${safeHeight}&q=${quality}&f=webp`;
      }

      return url;
    },

    normalizeImageInput(imageInput) {
      if (!imageInput) {
        return { imageId: null, url: '', image: null };
      }

      if (typeof imageInput === 'string') {
        return {
          imageId: this.extractImageIdFromUrl(imageInput),
          url: imageInput,
          image: null
        };
      }

      const candidateUrl = imageInput.optimizedUrl || imageInput.displayUrl || imageInput.url || '';
      const imageId = imageInput.id || imageInput.imageId || this.extractImageIdFromUrl(candidateUrl);

      return {
        imageId: imageId || null,
        url: candidateUrl,
        image: imageInput
      };
    },

    extractImageIdFromUrl(url) {
      if (!url || typeof url !== 'string') return null;
      const cleanUrl = url.split('?')[0];
      const match = cleanUrl.match(/(?:\b|\/)(\d+)(?=\.[a-z]+$)/i);
      return match ? parseInt(match[1], 10) : null;
    },

    async fetchVariantUrl({ imageId, cacheKey, width, height, context, fallbackUrl, imageRef }) {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        if (cacheKey) {
          this.pendingVariantRequests.delete(cacheKey);
        }
        return;
      }
      
      if (!imageId) {
        if (cacheKey) {
          this.pendingVariantRequests.delete(cacheKey);
        }
        return;
      }

      // 如果请求的是原图，直接使用原图URL
      if (context === 'original') {
        const originalUrl = fallbackUrl; // fallbackUrl 就是原图URL
        if (cacheKey) {
          this.$set(this.imageVariantCache, cacheKey, originalUrl);
        }
        this.applyOptimizedUrlToImage(imageId, originalUrl, imageRef);
        this.pendingVariantRequests.delete(cacheKey);
        return;
      }

      // 创建取消令牌
      const cancelToken = axios.CancelToken.source();
      
      try {
        const response = await apiClient.get(`/image-variants/best/${imageId}`, {
          params: {
            variant: this.getVariantForContext(context),
            width,
            height,
            preferWebp: true
          },
          cancelToken: cancelToken.token
        });

        if (response && response.success && response.data) {
          // 优先使用 bestUrl，如果没有则使用 originalUrl
          const bestUrl = response.data.bestUrl || response.data.originalUrl;
          if (bestUrl) {
            this.$set(this.imageVariantCache, cacheKey, bestUrl);
            this.applyOptimizedUrlToImage(imageId, bestUrl, imageRef);
            return;
          }
        }
      } catch (error) {
        // 如果是取消的请求，不显示警告
        if (axios.isCancel(error)) {
          console.log(`图片 ${imageId} 变体请求已取消`);
          return;
        }
        
        const message = error && error.message ? error.message : error;
        console.warn(`获取图片 ${imageId} 变体失败，使用原图`, message);
      } finally {
        this.pendingVariantRequests.delete(cacheKey);
      }

      if (cacheKey && !this.imageVariantCache[cacheKey]) {
        // ⭐ 卡片场景使用高质量
        const fallback = this.buildFallbackImageUrl(fallbackUrl, width, height, context === 'card');
        this.$set(this.imageVariantCache, cacheKey, fallback);
        this.applyOptimizedUrlToImage(imageId, fallback, imageRef);
      }
    },

    applyOptimizedUrlToImage(imageId, optimizedUrl, imageRef) {
      if (!optimizedUrl || !imageId) return;

      const updateImage = (img) => {
        if (!img) return;
        const candidateId = img.id || img.imageId;
        if (candidateId && Number(candidateId) === Number(imageId)) {
          if (img.optimizedUrl !== optimizedUrl) {
            this.$set(img, 'optimizedUrl', optimizedUrl);
          }
          if (img.displayUrl !== optimizedUrl) {
            this.$set(img, 'displayUrl', optimizedUrl);
          }
        }
      };

      updateImage(imageRef);

      const collections = [this.displayModels, this.latestModels, this.allModelsData];
      collections.forEach(models => {
        if (!Array.isArray(models)) return;
        models.forEach(model => {
          if (!model || !Array.isArray(model.Images)) return;
          model.Images.forEach(updateImage);
        });
      });
    },
    
    // 预加载品牌logo
    preloadBrandLogos() {
      if (!this.allBrands) return;
      
      // 创建一个Map来存储已加载的图片
      const loadedImages = new Map();
      
      this.allBrands.forEach(brand => {
        if (brand.logo && !loadedImages.has(brand.logo)) {
          const img = new Image();
          img.onload = () => {
            loadedImages.set(brand.logo, true);
          };
          img.onerror = () => {
            this.$set(this.logoLoadError, brand.id, true);
          };
          img.src = brand.logo;
        }
      });
    },

    // 初始化品牌数据
    async initializeBrands() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        return;
      }
      
      try {
        await this.fetchChineseBrands();
        // 品牌区已使用原生 lazy loading，避免首页初始化时重复预加载所有 logo。
      } catch (error) {
        console.error('初始化品牌数据失败:', error);
      }
    },
    // 设置品牌分类
    setBrandCategory(category) {
      this.brandCategory = category;
      this.currentLetter = '不限'; // 重置字母筛选
    },
    // 获取当前品牌分类的名称
    getCategoryName() {
      if (this.brandCategory === 'all') {
        return this.$t('home.allBrands');
      } else if (this.brandCategory === 'domestic') {
        return this.$t('home.domesticBrands');
      } else if (this.brandCategory === 'overseas') {
        return this.$t('home.overseasBrands');
      }
      return '';
    },
    // 处理品牌logo加载失败
    handleBrandLogoError(event) {
      const img = event.target;
      img.style.display = 'none';
      img.nextElementSibling.style.display = 'flex'; // 显示no-logo
    },
         // 获取占位符图片URL
     getPlaceholderImage() {
       // 使用轻量级的SVG占位符，避免额外的HTTP请求
       return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iNCIgZmlsbD0iI0Y1RjVGNSIvPgo8L3N2Zz4K';
     },

    handleLogoLoad(event) {
      const img = event.target;
      img.classList.add('loaded');
      // 获取品牌ID
      const brandId = this.getBrandIdFromImg(img);
      if (brandId) {
        this.$set(this.logoLoadError, brandId, false);
      }
    },

    handleLogoError(event) {
      const img = event.target;
      img.style.display = 'none';
      // 获取品牌ID
      const brandId = this.getBrandIdFromImg(img);
      if (brandId) {
        this.$set(this.logoLoadError, brandId, true);
      }
    },

    // 获取轮播图内容（最新上传的车型）
    async fetchCarouselModels() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        return;
      }
      
      this.latestModelsLoading = true;
      this.latestModelsError = null;
      
      try {
        console.log('开始获取轮播图内容...');
        
        // 获取最新上传的车型数据 - 减少数量提升速度
        const modelsResponse = await modelAPI.getAll({
          latest: true,
          limit: 6, // 减少到6个车型，提升加载速度
          page: 1,
          sortOrder: 'desc', // 降序排列
          sortBy: 'createdAt' // 按创建时间排序，展示最新上传的车型
        });
        
        console.log('轮播图车型API响应:', modelsResponse);
        
        // 处理车型数据
        if (modelsResponse && modelsResponse.data && Array.isArray(modelsResponse.data)) {
          this.latestModels = modelsResponse.data;
        } else if (Array.isArray(modelsResponse)) {
          this.latestModels = modelsResponse;
        } else {
          console.warn('无效的车型响应格式:', modelsResponse);
          this.latestModels = [];
        }
        
        // 只展示车型到轮播项目中
        this.carouselItems = this.latestModels.map(model => ({ ...model, type: 'model' }));
        
        console.log('获取到轮播图内容:', this.carouselItems);
        console.log('最新上传车型数量:', this.latestModels.length);
        
        // 如果有内容，启动自动播放
        if (this.carouselItems.length > 0) {
          this.startAutoPlay();
        } else {
          console.warn('没有可用的轮播内容');
        }
      } catch (error) {
        console.error('获取轮播图内容失败:', error);
        this.latestModelsError = error.message || '获取轮播图内容失败';
      } finally {
        this.latestModelsLoading = false;
      }
    },

    // 获取首页资讯
    async loadHomeArticles() {
      if (!this.isComponentActive) {
        return;
      }

      this.homeArticlesLoading = true;
      this.homeArticlesError = null;

      try {
        const latestResult = await apiClient.get('/articles', {
          params: {
            limit: 6,
            sortBy: 'publishedAt',
            sortOrder: 'desc'
          }
        });

        if (latestResult && latestResult.status === 'success') {
          this.homeArticles = latestResult.data.articles || [];
          const articleTotal = Number(latestResult.data.pagination && latestResult.data.pagination.total);
          this.articleTotalCount = Number.isFinite(articleTotal)
            ? articleTotal
            : this.homeArticles.length;
          this.homeFeaturedArticles = [];
        } else {
          this.homeArticlesError = (latestResult && latestResult.message) || '资讯加载失败，请稍后重试';
        }
      } catch (error) {
        console.error('加载首页资讯失败:', error);
        this.homeArticlesError = '资讯加载失败，请稍后重试';
      } finally {
        this.homeArticlesLoading = false;
      }
    },

    // 文章时间格式化
    formatArticleDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN');
    },
    
    // 轮播图控制方法
    startAutoPlay() {
      if (this.autoPlayInterval || this.carouselItems.length <= 1) {
        return;
      }

      this.autoPlayInterval = setInterval(() => {
        if (!document.hidden && this.isComponentActive) {
          this.nextSlide(false);
        }
      }, 5000);
    },
    
    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    },

    resetAutoPlay() {
      this.stopAutoPlay();
      this.startAutoPlay();
    },

    nextSlide(shouldResetAutoPlay = true) {
      if (this.carouselItems.length > 1) {
        this.currentSlide = (this.currentSlide + 1) % this.carouselItems.length;
        if (shouldResetAutoPlay) {
          this.resetAutoPlay();
        }
      }
    },
    
    prevSlide() {
      if (this.carouselItems.length > 1) {
        this.currentSlide = this.currentSlide === 0 
          ? this.carouselItems.length - 1 
          : this.currentSlide - 1;
        this.resetAutoPlay();
      }
    },
    
    goToSlide(index) {
      this.currentSlide = index;
      this.resetAutoPlay();
    },
    
    // 获取车型展示数据 - 添加缓存机制
    async fetchDisplayModels() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        console.log('组件已停止，跳过车型展示数据加载');
        return;
      }
      
      this.displayModelsLoading = true;
      this.displayModelsError = null;
      // 如果是第一页，重置图片加载错误状态
      if (this.currentDisplayPage === 1) {
        this.modelImageLoadError = {};
      }
      
      try {
        console.log('开始获取车型展示数据...');
        console.log('排序类型:', this.sortType);
        console.log('年代筛选:', this.selectedDecade);
        
        // 检查缓存
        const cacheKey = `${this.sortType}_${this.selectedDecade}_${this.showConceptCars}_${this.currentDisplayPage}`;
        const now = Date.now();
        const cacheValidTime = 3 * 60 * 1000; // 3分钟缓存
        
        if (this.dataCache.displayModels.has(cacheKey) && 
            this.dataCache.displayModelsCacheTime.has(cacheKey) &&
            (now - this.dataCache.displayModelsCacheTime.get(cacheKey)) < cacheValidTime) {
          console.log('使用缓存的车型展示数据');
          
          // 临时禁用滚动监听器，避免页面高度变化触发重复加载
          this.removeScrollListener();
          
          const cachedData = this.dataCache.displayModels.get(cacheKey);
          if (this.currentDisplayPage === 1) {
            this.displayModels = cachedData;
          } else {
            this.displayModels = [...this.displayModels, ...cachedData];
          }
          this.displayModelsLoading = false;
          
          // 使用缓存数据时，需要更新hasMoreDisplayModels状态
          // 只有当缓存数据长度等于页面大小时，才可能有更多数据
          this.hasMoreDisplayModels = cachedData.length === this.displayPageSize;
          
          // 添加调试日志
          console.log(`缓存数据长度: ${cachedData.length}, 页面大小: ${this.displayPageSize}, 当前页: ${this.currentDisplayPage}`);
          console.log(`hasMoreDisplayModels 设置为: ${this.hasMoreDisplayModels}`);
          
          // 延迟重新观察懒加载图片，避免立即触发滚动监听
          this.$nextTick(() => {
            setTimeout(() => {
              this.observeLazyImages();
              // 延迟启用滚动监听器，避免页面高度变化立即触发
              setTimeout(() => {
                if (this.isComponentActive) {
                  console.log('缓存数据加载完成，重新启用滚动监听');
                  this.addScrollListener();
                }
              }, 500);
            }, 100);
          });
          return;
        }
        
        // ⭐ 禁用滚动位置恢复功能，提升页面加载性能
        // 不再检查是否需要恢复滚动位置，始终从第一页开始加载
        let needMoreData = false;
        let estimatedPages = 1;
        
        // 构建API请求参数
        // 根据排序类型设置 sortBy 和 sortOrder
        let sortBy = 'year';
        let sortOrder = 'desc';
        
        if (this.sortType === 'newest') {
          // 新车优先：按年份降序
          sortBy = 'year';
          sortOrder = 'desc';
        } else if (this.sortType === 'oldest') {
          // 老车优先：按年份升序
          sortBy = 'year';
          sortOrder = 'asc';
        } else if (this.sortType === 'latestUpload') {
          // 最新上传：按创建时间降序，最新上传的在最前面
          sortBy = 'createdAt';
          sortOrder = 'desc';
        }
        
        const params = {
          limit: this.displayPageSize,
          page: this.currentDisplayPage,
          sortOrder: sortOrder,
          sortBy: sortBy
        };
        
        // 如果选择了年代筛选，添加年代参数
        if (this.selectedDecade) {
          params.decade = this.selectedDecade;
        }
        
        // 如果选择了概念车筛选，添加概念车参数
        if (this.showConceptCars) {
          params.concept = 'true';
        }
        
        const response = await modelAPI.getAll(params);
        
        console.log('车型展示API响应:', response);
        
        // 处理不同的响应格式
        if (response && response.data && Array.isArray(response.data)) {
          const newModels = response.data;
          const modelTotal = Number(response.total);
          if (Number.isFinite(modelTotal)) {
            this.modelTotalCount = modelTotal;
          }
          if (this.currentDisplayPage === 1) {
            this.displayModels = newModels;
          } else {
            this.displayModels = [...this.displayModels, ...newModels];
          }
          this.hasMoreDisplayModels = newModels.length === this.displayPageSize;
          // 再次检查组件是否仍然活跃
          if (!this.isComponentActive) {
            console.log('组件已停止，跳过车型展示数据处理');
            return;
          }
          
          console.log('获取到车型展示数据:', this.displayModels);
          
          // 缓存数据
          this.dataCache.displayModels.set(cacheKey, newModels);
          this.dataCache.displayModelsCacheTime.set(cacheKey, now);
          
          // 预加载下一批图片
          this.preloadNextBatchImages(newModels);
          
          // 重新观察懒加载图片
          this.$nextTick(() => {
            this.observeLazyImages();
          });
        } else if (Array.isArray(response)) {
          const newModels = response;
          if (this.currentDisplayPage === 1) {
            this.displayModels = newModels;
          } else {
            this.displayModels = [...this.displayModels, ...newModels];
          }
          this.hasMoreDisplayModels = newModels.length === this.displayPageSize;
          console.log('获取到车型展示数据(数组格式):', this.displayModels);
          
          // 预加载下一批图片
          this.preloadNextBatchImages(newModels);
          
          // 重新观察懒加载图片
          this.$nextTick(() => {
            this.observeLazyImages();
          });
        } else {
          console.error('无效的车型展示响应格式:', response);
          throw new Error('获取车型展示数据失败');
        }
      } catch (error) {
        console.error('获取车型展示数据失败:', error);
        this.displayModelsError = error.message || '获取车型展示数据失败';
        
        // 增加错误重试计数
        this.errorRetryCount++;
        
        // 如果重试次数未达到上限，延迟重试
        if (this.errorRetryCount < this.maxRetryCount) {
          console.log(`将在 ${this.retryDelay}ms 后重试 (${this.errorRetryCount}/${this.maxRetryCount})`);
          setTimeout(() => {
            this.retryFetchDisplayModels();
          }, this.retryDelay);
        } else {
          console.error('已达到最大重试次数，停止自动重试');
          this.hasMoreDisplayModels = false;
        }
      } finally {
        this.displayModelsLoading = false;
      }
    },
    
    // 选择排序方式
    selectSortType(type) {
      this.sortType = type;
      console.log('选择排序方式:', type);
      this.currentDisplayPage = 1;
      // 重置图片加载错误状态
      this.modelImageLoadError = {};
      this.fetchDisplayModels();
    },
    
    // 选择年代筛选
    selectDecade(decade) {
      this.selectedDecade = decade;
      console.log('选择年代筛选:', decade);
      this.currentDisplayPage = 1;
      // 重置图片加载错误状态
      this.modelImageLoadError = {};
      this.fetchDisplayModels();
    },
    
    // 切换概念车筛选
    toggleConceptCars() {
      this.showConceptCars = !this.showConceptCars;
      console.log('切换概念车筛选:', this.showConceptCars);
      this.currentDisplayPage = 1;
      // 重置图片加载错误状态
      this.modelImageLoadError = {};
      this.fetchDisplayModels();
    },
    
    // 加载更多车型
    loadMoreModels() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        return;
      }
      
      // 检查冷却时间，防止重复加载
      const now = Date.now();
      if (now - this.lastLoadTime < this.loadCooldown) {
        console.log('加载冷却中，跳过此次加载');
        return;
      }
      
      if (this.hasMoreDisplayModels && !this.displayModelsLoading && !this.displayModelsError) {
        // 检查并发请求限制
        if (this.activeRequests >= this.maxConcurrentRequests) {
          console.log('请求队列已满，跳过此次加载');
          return;
        }
        
        this.lastLoadTime = now;
        this.currentDisplayPage++;
        this.activeRequests++;
        
        this.fetchDisplayModels().then(() => {
          // 新加载的车型图片也需要懒加载
          this.observeLazyImages();
        }).catch(error => {
          console.error('加载更多车型失败:', error);
          // 发生错误时停止自动加载
          this.hasMoreDisplayModels = false;
        }).finally(() => {
          this.activeRequests--;
        });
      }
    },
    
    // 重试获取车型数据
    retryFetchDisplayModels() {
      this.currentDisplayPage = 1;
      this.displayModels = [];
      // 重置图片加载错误状态
      this.modelImageLoadError = {};
      // 重置错误状态，允许重新加载
      this.displayModelsError = null;
      this.hasMoreDisplayModels = true;
      // 重置错误重试计数
      this.errorRetryCount = 0;
      this.fetchDisplayModels().then(() => {
        this.observeLazyImages();
      });
    },
    
    // 重试单个图片加载
    retryImageLoad(model) {
      if (model.Images && model.Images.length > 0) {
        // 重置错误状态
        this.$set(this.modelImageLoadError, model.id, false);
        
        // 找到对应的图片元素并重新加载
        this.$nextTick(() => {
          const img = document.querySelector(`img[alt="${model.name}"]`);
          if (img) {
            // 隐藏占位符
            const placeholder = img.parentElement.querySelector('.model-display-placeholder');
            if (placeholder) {
              placeholder.classList.remove('show');
            }
            // 显示图片元素
            img.style.display = 'block';
            // 重新加载图片 - 使用优化的尺寸（400x300）
            img.src = this.getOptimizedImageUrl(model.Images[0], 400, 300);
          }
        });
      }
    },

    // 处理车型图片加载成功
    handleModelImageLoad(event) {
      const img = event.target;
      img.classList.add('loaded');
      // 隐藏占位符
      const placeholder = img.parentElement.querySelector('.model-display-placeholder');
      if (placeholder) {
        placeholder.classList.remove('show');
      }
      // 获取车型ID
      const modelId = this.getModelIdFromImg(img);
      if (modelId) {
        this.$set(this.modelImageLoadError, modelId, false);
      }
    },

    // 处理车型图片加载失败
    handleModelImageError(event) {
      const img = event.target;
      img.style.display = 'none';
      // 显示占位符
      const placeholder = img.parentElement.querySelector('.model-display-placeholder');
      if (placeholder) {
        placeholder.classList.add('show');
      }
      // 获取车型ID
      const modelId = this.getModelIdFromImg(img);
      if (modelId) {
        this.$set(this.modelImageLoadError, modelId, true);
      }
    },

    // 从图片元素获取车型ID
    getModelIdFromImg(img) {
      // 查找车型展示卡片
      let modelCard = img.closest('.model-display-card');
      if (modelCard) {
        return modelCard.getAttribute('data-model-id');
      }
      // 如果没有找到，尝试查找普通的车型卡片
      modelCard = img.closest('.model-card');
      if (modelCard) {
        return modelCard.getAttribute('data-model-id');
      }
      return null;
    },

    getBrandIdFromImg(img) {
      // 通过DOM遍历找到对应的brand-card元素
      let current = img;
      while (current && !current.classList.contains('brand-card')) {
        current = current.parentElement;
      }
      if (current) {
        return current.dataset.brandId;
      }
      return null;
    },

    // 重置logo加载状态
    resetLogoLoadState() {
      this.logoLoadError = {};
      // 移除所有loaded类
      const logos = document.querySelectorAll('.brand-logo-img');
      logos.forEach(logo => {
        logo.classList.remove('loaded');
        logo.style.display = '';
      });
    },

    getKnowledgeScrollContainer() {
      if (typeof window !== 'undefined' && window.innerWidth <= 960) {
        return null;
      }
      return this.$refs.knowledgeMain || null;
    },

    scrollToSection(sectionId) {
      const container = this.getKnowledgeScrollContainer();
      const scope = container || this.$el;
      const target = scope && scope.querySelector(`#${sectionId}`);
      if (!target) {
        return;
      }

      if (container) {
        const containerTop = container.getBoundingClientRect().top;
        const targetTop = target.getBoundingClientRect().top;
        container.scrollTo({
          top: container.scrollTop + (targetTop - containerTop) - 12,
          behavior: 'smooth'
        });
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    // 添加滚动监听
    addScrollListener() {
      if (this.handleScroll) {
        return;
      }

      this.handleScroll = () => {
        if (this.scrollRafId) {
          return;
        }

        this.scrollRafId = window.requestAnimationFrame(() => {
          this.scrollRafId = null;
          this.checkScrollPosition();
        });
      };

      this.scrollListenerTarget = this.getKnowledgeScrollContainer() || window;
      this.scrollListenerTarget.addEventListener('scroll', this.handleScroll, { passive: true });
    },

    // 移除滚动监听
    removeScrollListener() {
      if (this.handleScroll && this.scrollListenerTarget) {
        this.scrollListenerTarget.removeEventListener('scroll', this.handleScroll);
        this.handleScroll = null;
        this.scrollListenerTarget = null;
      }

      if (this.scrollRafId) {
        window.cancelAnimationFrame(this.scrollRafId);
        this.scrollRafId = null;
      }
    },
    
    // 停止所有自动加载
    stopAllAutoLoading() {
      // 设置组件为非活跃状态
      this.isComponentActive = false;
      
      // 停止滚动监听
      this.removeScrollListener();
      
      // 停止轮播图自动播放
      this.stopAutoPlay();
      
      // 停止所有加载状态
      this.displayModelsLoading = false;
      this.latestModelsLoading = false;
      
      // 停止更多数据加载
      this.hasMoreDisplayModels = false;
      this.hasMoreModels = false;
      
      // 重置错误状态
      this.displayModelsError = null;
      this.latestModelsError = null;
      
      // 重置请求计数
      this.activeRequests = 0;
      this.errorRetryCount = 0;
      
      // 清理所有待处理的图片变体请求
      this.pendingVariantRequests.clear();
      this.imageVariantCache = {};
      
      // 清理变体请求队列
      this.variantRequestQueue = [];
      this.activeVariantRequests = 0;
      
      // 清理懒加载观察器
      if (this.lazyLoadObserver) {
        this.lazyLoadObserver.disconnect();
        this.lazyLoadObserver = null;
      }
      
      // 取消所有待处理的变体请求
      this.cancelAllVariantRequests();
      
      console.log('已停止所有自动加载机制');
    },
    
    // 取消所有变体请求
    cancelAllVariantRequests() {
      // 取消所有待处理的变体请求
      this.variantRequestQueue.forEach(request => {
        if (request.cancelToken) {
          request.cancelToken.cancel('Component destroyed');
        }
      });
      this.variantRequestQueue = [];
      
      // 清理所有待处理的变体请求
      this.pendingVariantRequests.clear();
      
      console.log('已取消所有变体请求');
    },

    // 检查滚动位置 - 优化触发条件
    checkScrollPosition() {
      // 检查组件是否仍然活跃
      if (!this.isComponentActive) {
        return;
      }
      
      // 如果正在初始化，跳过
      if (this.isInitializing) {
        return;
      }

      // 首批车型还没渲染时不要触发“加载更多”，避免首次滚动误判接近底部。
      if (this.displayModels.length === 0) {
        return;
      }
      
      // 移除路由检查，允许图片变体请求功能
      // if (this.$route.path !== '/') {
      //   console.log('不在首页，停止滚动监听');
      //   this.removeScrollListener();
      //   return;
      // }
      
      // 如果正在加载或没有更多数据，则不执行
      if (this.displayModelsLoading || !this.hasMoreDisplayModels) {
        return;
      }

      // 如果最近有错误，暂停自动加载
      if (this.displayModelsError) {
        return;
      }

      // 检查组件是否还存在（防止在组件销毁后继续执行）
      if (!this.$el || !document.contains(this.$el)) {
        console.log('组件已销毁，停止滚动监听');
        this.removeScrollListener();
        return;
      }

      const scrollContainer = this.getKnowledgeScrollContainer();
      let scrollTop;
      let viewportHeight;
      let scrollHeight;

      if (scrollContainer) {
        scrollTop = scrollContainer.scrollTop;
        viewportHeight = scrollContainer.clientHeight;
        scrollHeight = scrollContainer.scrollHeight;
      } else {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        viewportHeight = window.innerHeight;
        scrollHeight = document.documentElement.scrollHeight;
      }

      // 当滚动到距离底部800px时触发加载，给用户更多缓冲时间
      const threshold = 800;
      if (scrollTop + viewportHeight >= scrollHeight - threshold) {
        console.log('触发自动加载更多车型');
        this.loadMoreModels();
      }
    },

    // 节流函数
    throttle(func, delay) {
      let timeoutId;
      let lastExecTime = 0;
      return function (...args) {
        const currentTime = Date.now();
        
        if (currentTime - lastExecTime > delay) {
          func.apply(this, args);
          lastExecTime = currentTime;
        } else {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            func.apply(this, args);
            lastExecTime = Date.now();
          }, delay - (currentTime - lastExecTime));
        }
      };
    },
    
    // 处理反馈提交
    handleFeedbackSubmitted(feedbackData) {
      console.log('收到用户反馈:', feedbackData);
      // 可以在这里添加统计逻辑或其他处理
      this.$message.success(this.$t('home.thanksFeedback'));
    }
  },
  
  beforeRouteLeave(to, from, next) {
    // 在离开首页时立即停止所有活动
    console.log('离开首页，立即停止所有活动');
    this.stopAllAutoLoading();
    next();
  },
  async mounted() {
    // 移除路由检查，允许图片变体请求功能
    // if (this.$route.path !== '/') {
    //   console.log('不在首页，跳过首页初始化');
    //   return;
    // }
    
    // 设置组件状态为活跃
    this.isComponentActive = true;
    
    // 先让页面具备滚动能力，非首屏数据放到空闲时加载，避免首次滚动被批量渲染抢占。
    this.addScrollListener();
    this.initLazyLoading();

    this.fetchCarouselModels()
      .catch(error => {
        console.error('轮播图数据加载失败:', error);
      });

    setTimeout(() => {
      this.runWhenIdle(() => {
        if (!this.isComponentActive) return;
        this.initializeBrands().catch(error => {
          console.error('品牌数据加载失败:', error);
        });
      }, 1000);
    }, 700);

    setTimeout(() => {
      this.runWhenIdle(() => {
        if (!this.isComponentActive) return;
        this.fetchDisplayModels()
          .then(() => this.observeLazyImages())
          .catch(error => {
            console.error('车型展示数据加载失败:', error);
          });
      }, 1200);
    }, 900);

    setTimeout(() => {
      this.runWhenIdle(() => {
        if (!this.isComponentActive) return;
        this.loadHomeArticles().catch(error => {
          console.error('首页资讯加载失败:', error);
        });
      }, 1400);
    }, 1100);
    
    // 标记初始化完成
    this.isInitializing = false;
  },
  
  beforeDestroy() {
    // 设置组件为非活跃状态
    this.isComponentActive = false;
    
    // 停止所有自动加载
    this.stopAllAutoLoading();
    
    // 清理懒加载观察器
    if (this.lazyLoadObserver) {
      this.lazyLoadObserver.disconnect();
    }
    
    // 清理所有定时器
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
    
    console.log('首页组件销毁，已清理所有资源');
  },
  watch: {
    // 监听路由变化，重置logo加载状态
    '$route'(to, from) {
      this.resetLogoLoadState();
      
      // 如果离开首页，立即停止所有自动加载
      if (from.path === '/' && to.path !== '/') {
        console.log('离开首页，停止所有自动加载');
        this.stopAllAutoLoading();
      }
    }
  }
}
</script>

<style scoped>
.home {
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  overflow-x: clip;
  overflow-y: visible;
  width: 100%;
  background: #0a0a0a;
  min-height: 100vh;
}

/* 顶部导航栏样式 */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fff;
}

.logo-nav {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.menu-nav {
  display: flex;
}

.menu-nav a {
  margin-left: 25px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  text-decoration: none;
}

.menu-nav a:hover {
  color: #e03426;
}

.menu-nav a.router-link-active {
  color: #e03426;
  font-weight: bold;
}

/* 全屏轮播图样式 */
/* 全局内容容器 - 自适应布局 */
.content-container {
  width: 100%;
  margin: 0 auto;
  padding: 0 12px;
  box-sizing: border-box;
  max-width: 1400px;
}

.fullscreen-carousel {
  position: relative;
  width: 100%;
  height: 70vh;
  overflow: hidden;
  background: #000;
  margin: 0 0 20px 0;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  touch-action: pan-y;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.35s ease;
}

.carousel-slide.active {
  opacity: 1;
  visibility: visible;
}

/* 轮播图整块为链接，右击显示浏览器原生“在新标签页中打开链接” */
.carousel-slide-link,
.carousel-slide-panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  text-decoration: none;
  color: inherit;
  z-index: 1;
  cursor: pointer;
}

.carousel-slide-panel {
  cursor: default;
}

.slide-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.slide-placeholder {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  align-items: center;
  justify-content: center;
  color: white;
}

.slide-placeholder.show {
  display: flex;
}

.placeholder-content {
  text-align: center;
}

.placeholder-content i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.7;
}

.placeholder-content span {
  font-size: 18px;
  font-weight: 500;
}

.slide-info-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 60px 12px 40px;
  z-index: 10;
}

.slide-content {
  max-width: none;
  margin: 0;
  padding: 0 80px 0 80px; /* 为文字内容添加左右边距，避免被导航按钮遮挡 */
  box-sizing: border-box;
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important; /* 强制所有子元素左对齐 */
  text-align: left !important; /* 确保文本左对齐 */
  justify-content: flex-start !important; /* 强制内容靠左 */
  width: 100% !important; /* 确保容器宽度 */
}

.content-type-badge {
  display: inline-block;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 20px;
  margin-left: 0 !important; /* 强制与车型名左对齐 */
  margin-right: auto !important; /* 确保按钮靠左 */
  align-self: flex-start !important; /* 确保按钮左对齐 */
  text-align: left !important; /* 强制左对齐 */
  width: fit-content !important; /* 让按钮宽度适应内容 */
  position: relative !important; /* 使用相对定位 */
  left: 0 !important; /* 强制左对齐 */
}

.model-badge {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
}



.slide-title {
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  text-align: left; /* 确保车型名左对齐 */
}

.slide-brand {
  font-size: 24px;
  margin: 0 0 20px 0;
  opacity: 0.9;
  font-weight: 300;
  text-align: left; /* 确保品牌名左对齐 */
}

.view-details-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.view-details-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

/* 导航指示器 */
.carousel-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 20;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: white;
  transform: scale(1.2);
}



/* 左右导航按钮 */
.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.carousel-nav:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-50%) scale(1.1);
}

.carousel-nav.prev {
  left: 20px;
}

.carousel-nav.next {
  right: 20px;
}

/* 加载和错误状态 */
.loading-container,
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: white;
  text-align: center;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.loading-spinner i {
  font-size: 48px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
}

.error-text {
  font-size: 18px;
  font-weight: 500;
}

/* 车型展示区域样式 */
.models-display-section {
  background: transparent;
  margin: 8px 0 20px 0;
  border-radius: 12px;
  overflow: hidden;
}

.section-header {
  padding: 0;
  border-bottom: none;
  text-align: center;
  display: none; /* 隐藏标题区域，让筛选栏直接显示 */
}

.section-title {
  text-align: center;
}

.section-title h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
}

.section-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* 旧的 sort-buttons 已移除 */

/* 旧的按钮样式已合并到 .filter-btn */

/* 筛选控制栏样式 - 单行版 */
.filter-control-bar {
  padding: 18px 0;
  border-bottom: none;
  background: transparent;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.filter-control-bar::-webkit-scrollbar {
  display: none;
}

/* 筛选分隔符 */
.filter-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 6px;
  flex-shrink: 0;
}

/* 统一的筛选按钮样式 */
.filter-btn {
  padding: 6px 14px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  text-align: center;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-btn:hover {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.5);
  color: #ffffff;
}

.filter-btn.active {
  background: #e03426;
  border-color: #e03426;
  color: white;
  box-shadow: 0 2px 8px rgba(224, 52, 38, 0.4);
}

.filter-btn:active {
  transform: scale(0.97);
}

.filter-btn.icon-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-btn.icon-btn i {
  font-size: 12px;
}

.models-content {
  padding: 12px 8px 16px 8px;
}

.models-content .models-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  padding: 0 4px;
}

.model-display-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.08);
  aspect-ratio: 4/5;
  min-height: 200px;
  /* ⭐ GPU加速优化 */
  transform: translateZ(0);
  backface-visibility: hidden;
  will-change: transform;
  /* 确保<a>标签显示为块级元素 */
  display: block;
  text-decoration: none;
  color: inherit;
}

.model-display-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(224, 52, 38, 0.3);
  border-color: #e03426;
}

.model-display-image {
  position: relative;
  height: 75%;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px 12px 0 0;
}

.model-display-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  /* ⭐ GPU加速优化 */
  transform: translateZ(0);
  will-change: transform;
}

.model-display-card:hover .model-display-img {
  transform: scale(1.05);
}

.model-display-placeholder {
  display: none;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ccc;
  font-size: 48px;
}

.model-display-placeholder.show {
  display: flex;
}

.model-display-info {
  padding: 10px 8px 12px 8px;
  height: 25%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  position: relative;
  z-index: 10;
  border-radius: 0 0 12px 12px;
}

.model-display-name {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.3;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 100%;
  width: 100%;
}



.load-more-container {
  text-align: center;
  margin-top: 30px;
}

.load-more-btn {
  background: #e03426;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
}

.load-more-btn:hover:not(:disabled) {
  background: #d02e20;
}

.load-more-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.hero-header {
  max-width: 1200px;
  margin: 0 auto 50px;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.hero-title h1 {
  font-size: 48px;
  font-weight: 700;
  color: white;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 300;
}

.view-all-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-all-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.hero-content .models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.model-hero-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  animation: slideInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(30px);
}

@keyframes slideInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.model-hero-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.25);
}

.model-image-container {
  position: relative;
  height: 280px;
  overflow: hidden;
}

.model-hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.model-hero-card:hover .model-hero-image {
  transform: scale(1.1);
}

.model-placeholder {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  align-items: center;
  justify-content: center;
  color: white;
}

.model-placeholder.show {
  display: flex;
}

.placeholder-content {
  text-align: center;
}

.placeholder-content i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.7;
}

.placeholder-content span {
  font-size: 16px;
  font-weight: 500;
}

.model-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.model-hero-card:hover .model-overlay {
  opacity: 1;
}

.overlay-content {
  color: white;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
}

.model-hero-info {
  padding: 30px;
  position: relative;
}

.model-badge {
  position: absolute;
  top: -15px;
  right: 30px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.model-hero-name {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.model-hero-brand {
  font-size: 16px;
  color: #666;
  margin: 0;
  font-weight: 500;
}

/* 加载状态样式 */
.loading-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.skeleton-card {
  height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 20px;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 错误和空状态样式 */
.error-container,
.empty-container {
  text-align: center;
  padding: 60px 20px;
  color: white;
}

.error-icon,
.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
}

.error-text,
.empty-text {
  font-size: 18px;
  font-weight: 500;
}

/* 品牌展示区域样式优化 */
.brand-section {
  background: transparent;
  margin: 20px 0 8px 0;
  border-radius: 0;
  overflow: hidden;
}

/* 品牌区域标题 */
.brand-section-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 0;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.03);
  position: relative;
  width: 100%;
}

.brand-section-header:hover {
  background: rgba(255, 255, 255, 0.06);
}

.brand-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.brand-section-title i {
  font-size: 20px;
  color: #e03426;
}

.brand-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
}

.toggle-icon {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
  transition: transform 0.3s ease;
  position: absolute;
  right: 20px;
}

.toggle-icon i {
  transition: transform 0.3s ease;
}

/* 品牌内容包裹器 */
.brand-content-wrapper {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 品牌分类标签页 */
.brand-category-tabs {
  display: flex;
  background: transparent;
  padding: 0;
  border-bottom: none;
  margin-bottom: 12px;
}

.category-tab {
  flex: 1;
  padding: 16px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
  position: relative;
}

.category-tab:hover {
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
}

.category-tab.active {
  background: transparent;
  color: #e03426;
  font-weight: 600;
}

.category-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #e03426;
}

/* 字母筛选器优化 */
.alphabet-filter {
  display: flex;
  flex-wrap: wrap;
  padding: 12px 16px;
  gap: 6px;
  background: transparent;
  border-bottom: none;
  justify-content: center;
}

.alphabet-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alphabet-btn:hover {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
  transform: translateY(-1px);
}

.alphabet-btn.active {
  background: #e03426;
  border-color: #e03426;
  color: white;
  box-shadow: 0 2px 6px rgba(224, 52, 38, 0.3);
}

.alphabet-btn:active {
  transform: scale(0.95);
  background: #fce4e4;
}

/* 品牌展示网格 */
.brands-display {
  padding: 16px 12px;
  background: transparent;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: min(0.8vw, 8px);
  margin-bottom: 16px;
  justify-items: center;
  align-items: start;
}

.brand-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: min(1vw, 8px) min(0.6vw, 6px);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: min(1vw, 8px);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  aspect-ratio: 1 / 1;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  max-width: 100px;
  height: auto;
  min-height: 0;
  padding-top: min(1.8vw, 14px);
}

.brand-card-link {
  text-decoration: none;
  color: inherit;
}

.brand-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(224, 52, 38, 0.3);
  background: rgba(255, 255, 255, 0.08);
  border-color: #e03426;
}

.brand-card:active {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.brand-card .brand-logo {
  position: relative;
  width: clamp(20px, 3.2vw, 32px);
  height: clamp(20px, 3.2vw, 32px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: clamp(1px, 0.3vw, 4px);
  border-radius: clamp(2px, 0.5vw, 4px);
  flex-shrink: 0;
  background: #fff;
  overflow: hidden;
}

.brand-card .brand-logo-img {
  display: block;
  width: 80%;
  height: 80%;
  max-width: 80%;
  max-height: 80%;
  margin: auto;
  object-fit: contain;
  object-position: center;
}

.brand-logo::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  animation: loading-shimmer 1.5s infinite;
}

@keyframes loading-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.brand-logo img.loaded + .no-logo {
  display: none;
}

.brand-logo img.loaded {
  opacity: 1;
}

.brand-logo img.loaded ~ .loading-shimmer {
  display: none;
}

.brand-logo-img {
  max-width: clamp(16px, 2.8vw, 28px);
  max-height: clamp(16px, 2.8vw, 28px);
  object-fit: contain;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.brand-logo-img.loaded {
  opacity: 1;
}

.brand-logo .no-logo {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e03426, #67C23A);
  color: white;
  font-size: clamp(6px, 1.2vw, 12px);
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.brand-logo .no-logo.show {
  opacity: 1;
}

.brand-card .brand-name {
  font-size: clamp(9px, 1.4vw, 13px);
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  line-height: 1.2;
  max-width: 100%;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: auto;
  padding: 0 clamp(1px, 0.3vw, 3px);
  min-height: clamp(16px, 2.8vw, 24px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  margin-bottom: clamp(1px, 0.3vw, 3px);
}

/* 品牌统计信息 */
.brand-stats {
  text-align: center;
  padding: 16px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  border-top: none;
  background: transparent;
  margin: 0 -20px -20px -20px;
}

.stats-text {
  margin-right: 8px;
}

.category-text {
  font-weight: 600;
  color: #e03426;
}

/* 旧的品牌展示样式已移除，使用新的样式 */

.loading-container {
  padding: 12px;
  margin-bottom: 12px;
}

.error-message {
  color: #F56C6C;
  text-align: center;
  padding: 12px;
}

.no-brands-message {
  text-align: center;
  padding: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
  cursor: pointer;
}

.model-card {
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
}

.model-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2) !important;
}

.bottom {
  margin-top: 13px;
  line-height: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.time {
  font-size: 13px;
  color: #999;
}

.button {
  padding: 0;
  float: right;
}

.el-divider {
  margin: 16px 0 12px 0;
}

.el-row {
  margin-bottom: 12px;
}

.load-more-section {
  text-align: center;
  margin-top: 16px;
  padding: 12px;
}

.load-more-btn {
  padding: 12px 32px;
  font-size: 16px;
  border-radius: 8px;
  background: #e03426;
  border: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(224, 52, 38, 0.3);
}

.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(224, 52, 38, 0.4);
}

.load-more-btn:active {
  transform: translateY(0);
}

.no-more-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: inline-block;
}

/* 移动端优化 */
* {
  -webkit-tap-highlight-color: transparent;
}

/* 防止水平滚动和确保页面背景 */
body, html {
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  background-color: #0a0a0a;
}

.home {
  background-color: #0a0a0a;
  min-height: 100vh;
  margin: 0;
  padding: 0;
}

.home * {
  box-sizing: border-box;
}

.brand-card, .alphabet-btn, .category-tab {
  -webkit-tap-highlight-color: rgba(224, 52, 38, 0.2);
  user-select: none;
}

/* 响应式设计 - 自适应padding */
@media (min-width: 1400px) {
  .content-container {
    padding: 0 20px;
  }
  
  .slide-info-overlay {
    padding: 60px 20px 40px;
  }
}

@media (max-width: 1200px) {
  .content-container {
    padding: 0 12px;
  }
  
  .slide-info-overlay {
    padding: 60px 12px 40px;
  }
}

@media (max-width: 1400px) {
.brands-grid {
    grid-template-columns: repeat(12, 1fr);
  }
}

@media (max-width: 1200px) {
  .brands-grid {
    grid-template-columns: repeat(10, 1fr);
  }
  
  .models-display-section {
    margin: 6px 15px;
    padding: 10px;
  }
  
  .filter-control-bar {
    padding: 14px 10px;
    gap: 5px;
    justify-content: flex-start;
    overflow-x: auto;
  }
  
  .filter-divider {
    margin: 0 4px;
  }
  
  .filter-btn {
    padding: 5px 10px;
    font-size: 11px;
  }
  
  .models-content .models-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }
}

@media (max-width: 768px) {
  .home {
    padding: 0;
  }
  
  .brand-filter-container,
  .models-display-section {
    margin: 4px 10px;
    padding: 8px;
    border-radius: 12px;
  }
  
  .filter-control-bar {
    padding: 12px 8px;
    gap: 4px;
    justify-content: flex-start;
    overflow-x: auto;
  }
  
  .filter-divider {
    margin: 0 3px;
  }
  
  .filter-btn {
    padding: 5px 9px;
    font-size: 10px;
  }
  
  
  .brand-category-filter {
    padding: 12px;
    gap: 6px;
  }

  .category-btn {
    padding: 6px 12px;
    font-size: 13px;
  }

  .letter-filter {
    padding: 12px;
    gap: 6px;
  }
  
  .letter-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .brands-container {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    gap: 8px;
    padding: 12px;
  }
  
  .brand-item {
    padding: 8px 4px;
  }
  
  .brand-logo {
    width: 28px;
    height: 28px;
  }
  
  .brand-logo img {
    max-width: 24px;
    max-height: 24px;
  }
  
  .no-logo {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
  
  .brand-name {
    font-size: 11px;
  }
  
  .brand-stats {
    padding: 10px 12px;
    font-size: 13px;
  }

  .stats-text {
    margin-right: 3px;
  }

  .category-text {
    font-size: 13px;
  }
  
  .section-header h2 {
    font-size: 20px;
  }
  
  .fullscreen-carousel {
    height: 60vh;
  }
  
  .slide-title {
    font-size: 36px;
    text-align: left; /* 移动端也保持左对齐 */
  }
  
  .slide-brand {
    font-size: 20px;
    text-align: left; /* 移动端也保持左对齐 */
  }
  
  .slide-info-overlay {
    padding: 50px 25px 35px;
  }
  
  .slide-content {
    padding: 0 60px 0 60px; /* 为文字内容添加左右边距 */
    display: flex;
    flex-direction: column;
    align-items: flex-start; /* 移动端也强制左对齐 */
  }
  
  .carousel-nav {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .carousel-nav.prev {
    left: 15px;
  }
  
  .carousel-nav.next {
    right: 15px;
  }
  
  .carousel-indicators {
    bottom: 20px;
  }
  
  .indicator {
    width: 10px;
    height: 10px;
  }
  
  .model-brand {
    font-size: 11px;
  }
  
  .model-series {
    font-size: 10px;
  }
  
  .models-content .models-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    padding: 0 2px;
  }
  
  .model-image,
  .model-display-image {
    height: 70% !important;
  }
  
  .model-info {
    padding: 12px;
  }
  
  .model-display-card {
    min-height: 160px !important;
  }
  
  .model-display-info {
    height: 30% !important;
    padding: 8px 6px 10px 6px !important;
    background: #fff !important;
    border-top: 1px solid #eee !important;
  }
  
  .model-display-name {
    font-size: 13px !important;
    line-height: 1.3 !important;
    color: #333 !important;
    font-weight: 600 !important;
    text-align: center !important;
    display: block !important;
  }
  
  .model-name {
    font-size: 15px;
  }
  
  .load-more-section {
    margin-top: 12px;
    padding: 10px;
  }
  
  .load-more-btn {
    padding: 10px 24px;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .content-container {
    padding: 0 12px;
  }
  
  .models-content {
    padding: 12px 6px 16px 6px;
  }
  
  .filter-control-bar {
    padding: 10px 5px;
    gap: 4px;
    justify-content: flex-start;
    overflow-x: auto;
  }
  
  .filter-divider {
    margin: 0 2px;
  }
  
  .filter-btn {
    padding: 4px 8px;
    font-size: 9px;
  }
  
  
  .decade-btn {
    padding: 6px 12px;
    font-size: 12px;
    min-width: 50px;
  }
  
  .slide-info-overlay {
    padding: 50px 10px 35px;
  }
  
  .slide-content {
    padding: 0 50px 0 50px; /* 为文字内容添加左右边距 */
  }
  
  .home {
    padding: 0;
  }
  
  .brand-section {
    margin: 0 0 20px 0;
    border-radius: 8px;
  }
  
  .brand-category-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  .brand-category-tabs::-webkit-scrollbar {
    display: none;
  }
  
  .category-tab {
    padding: 8px 12px;
    font-size: 12px;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: auto;
  }
  
  .alphabet-filter {
    padding: 12px 16px;
    gap: 4px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  .alphabet-filter::-webkit-scrollbar {
    display: none;
  }
  
  .alphabet-btn {
    min-width: 28px;
    height: 28px;
    font-size: 11px;
    flex-shrink: 0;
    padding: 0 6px;
  }
  
  .brands-display {
    padding: 12px 8px;
  }
  
  .brands-grid {
    grid-template-columns: repeat(8, 1fr);
    gap: 6px;
  }
  
  .brand-card {
    padding: 6px 3px;
    aspect-ratio: 1 / 1;
    max-width: none;
    height: auto;
    min-height: 0;
    padding-top: 12px;
  }
  
  .brand-card .brand-logo {
    width: 26px;
    height: 26px;
  }
  
  .brand-card .brand-logo img {
    max-width: 22px;
    max-height: 22px;
  }
  
  .brand-card .no-logo {
    width: 22px;
    height: 22px;
    font-size: 11px;
  }
  
  .brand-card .brand-name {
    font-size: 10px;
    color: #333333;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    margin-bottom: 3px;
  }
}

@media (max-width: 600px) {
  .brand-section {
    margin: 0 6px 16px 6px;
  }
  
  .brands-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 4px;
  }
  
  .brand-card {
    padding: 4px 2px;
    min-height: 70px;
    max-width: none;
    aspect-ratio: 1 / 1;
    height: auto;
    padding-top: 10px;
  }
  
  .brands-display {
    padding: 10px 4px;
  }
  
  .brand-card .brand-logo {
    width: 24px;
    height: 24px;
    margin-bottom: 2px;
  }
  
  .brand-card .brand-logo img {
    max-width: 20px;
    max-height: 20px;
  }
  
  .brand-card .no-logo {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
  
  .brand-card .brand-name {
    font-size: 10px;
    line-height: 1.1;
    min-height: 18px;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    color: #333333;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    margin-bottom: 1px;
  }
}

@media (max-width: 480px) {
  .content-container {
    padding: 0 8px;
  }
  
  .slide-info-overlay {
    padding: 40px 8px 30px;
  }
  
  .slide-content {
    padding: 0 40px 0 40px; /* 为文字内容添加左右边距 */
    display: flex;
    flex-direction: column;
    align-items: flex-start; /* 最小屏幕也强制左对齐 */
  }
  
  .slide-title,
  .slide-brand {
    text-align: left; /* 最小屏幕也保持左对齐 */
  }
  
  .brand-section,
  .latest-models-section {
    margin: 4px 0;
    border-radius: 6px;
  }
  
  .brand-section {
    padding: 0;
  }
  
  .brand-category-tabs {
    padding: 0;
    gap: 0;
  }
  
  .category-tab {
    padding: 6px 10px;
    font-size: 11px;
    border-radius: 0;
    min-width: 60px;
  }
  
  .alphabet-filter {
    padding: 6px 8px;
    gap: 2px;
  }
  
  .alphabet-btn {
    min-width: 20px;
    height: 20px;
    font-size: 8px;
    padding: 0 2px;
  }
  
  .brands-display {
    padding: 8px 4px;
  }
  
  .brands-grid {
    grid-template-columns: repeat(5, 1fr);
    gap: 3px;
  }
  
  .brand-card {
    padding: 6px 2px;
    aspect-ratio: 1 / 1;
    min-height: 60px;
    max-width: none;
    height: auto;
    padding-top: 10px;
  }
  
  .brand-card .brand-logo {
    width: 20px;
    height: 20px;
    margin-bottom: 4px;
  }
  
  .brand-card .brand-logo img {
    max-width: 16px;
    max-height: 16px;
  }
  
  .brand-card .no-logo {
    width: 16px;
    height: 16px;
    font-size: 8px;
  }
  
  .brand-card .brand-name {
    font-size: 9px;
    margin-top: auto;
    padding: 0 1px;
    line-height: 1.1;
    min-height: 18px;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    word-break: break-word;
    color: #333333;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    margin-bottom: 1px;
  }
  
  .models-content .models-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    padding: 0 4px;
  }
  
  .models-content {
    padding: 12px 6px 16px 6px;
  }
  
  .filter-control-bar {
    padding: 10px 6px;
    gap: 12px;
  }
  
  .decade-btn {
    padding: 5px 10px;
    font-size: 11px;
    min-width: 45px;
  }
  
  .sort-btn,
  .concept-btn {
    padding: 5px 10px;
    font-size: 11px;
  }
  
  .model-image,
  .model-display-image {
    height: 65% !important;
  }
  
  .model-name {
    font-size: 14px;
  }
  
  .model-display-name {
    font-size: 12px !important;
    line-height: 1.3 !important;
    height: auto !important;
    max-height: none !important;
    padding: 4px 6px !important;
    color: #333 !important;
    font-weight: 600 !important;
    text-align: center !important;
    display: block !important;
    overflow: visible !important;
    background: rgba(255, 255, 255, 0.9) !important;
    min-height: 20px !important;
  }
  
  .model-display-card {
    min-height: 180px !important;
    aspect-ratio: 1/1 !important;
  }
  
  .model-display-info {
    height: 35% !important;
    padding: 8px 6px 10px 6px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    background: #fff !important;
    border-top: 1px solid #eee !important;
    position: relative !important;
    z-index: 100 !important;
  }
  
  /* 确保骨架屏不会覆盖文字区域 */
  .model-image-skeleton {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 1 !important;
  }
  
  .brand-name {
    font-size: 12px;
  }
  
  .load-more-section {
    margin-top: 10px;
    padding: 8px;
  }
  
  .load-more-btn {
    padding: 8px 20px;
    font-size: 13px;
    width: 100%;
    max-width: 200px;
  }
  
  .no-more-text {
    font-size: 13px;
    padding: 10px;
  }
}

/* 懒加载图片样式 */
.lazy-load {
  opacity: 0.6;
  transition: opacity 0.4s ease;
  background: #f8f9fa;
}

.lazy-load.loaded {
  opacity: 1;
}

.lazy-load.error {
  opacity: 0.8;
  background: #f8f9fa;
}

/* 品牌logo专用样式 */
.brand-logo-img {
  background: #fff;
}

.brand-logo-img.loaded {
  opacity: 1;
  transform: scale(1);
}

.brand-logo-img.lazy-load {
  opacity: 0.3;
  transform: scale(0.95);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 图片加载动画 */
.model-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.model-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.model-card:hover .model-image img {
  transform: scale(1.02);
}

/* 车型图片骨架屏 */
.model-image-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.skeleton-shimmer {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #f8f9fa 25%,
    #e9ecef 50%,
    #f8f9fa 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 车型展示图片样式优化 */
.model-display-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.model-display-img.lazy-load {
  opacity: 0;
}

.model-display-img.loaded {
  opacity: 1;
}

.model-display-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f8f9fa;
  border-radius: 8px;
}

/* 重试按钮样式 */
.retry-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: #e03426;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.3s ease;
}

.retry-btn:hover {
  background: #f04838;
}

.retry-btn i {
  font-size: 16px;
}

/* 加载更多状态优化 */
.loading-more-container {
  text-align: center;
  padding: 20px;
  margin-top: 16px;
}

.loading-more-spinner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
}

.loading-more-spinner i {
  font-size: 16px;
  animation: rotating 1s linear infinite;
}

/* 图片加载失败时的重试按钮 */
.image-retry-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(224, 52, 38, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.model-display-image:hover .image-retry-btn {
  opacity: 1;
}

/* 优化加载动画 */
@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 加载状态指示器样式 */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  gap: 12px;
}

.loading-indicator i {
  font-size: 20px;
  animation: rotating 1s linear infinite;
}

/* 没有更多数据提示样式 */
.no-more-data {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;
}

/* 首页资讯区域 */
.home-articles-section {
  background: rgba(255, 255, 255, 0.02);
  padding: 32px 0 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.home-articles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.home-articles-title {
  text-align: center;
  width: 100%;
}

.home-articles-title h2 {
  margin: 0;
  font-size: 22px;
  color: rgba(255, 255, 255, 0.9);
}

.home-articles-title p {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
}

.home-articles-more {
  color: #e03426;
  font-weight: 600;
  padding: 0;
}

.home-articles-loading,
.home-articles-error,
.home-articles-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 30px 0;
  color: rgba(255, 255, 255, 0.65);
}

.home-articles-error {
  color: #f56c6c;
}

.home-articles-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.home-articles-footer {
  display: flex;
  justify-content: center;
  padding-top: 20px;
}

.home-featured-articles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.home-featured-card {
  display: flex;
  flex-direction: column;
  background: rgba(18, 18, 18, 0.95);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.home-featured-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.45);
}

.home-featured-image {
  position: relative;
  width: 100%;
  height: 180px;
  flex-shrink: 0;
}

.home-featured-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.home-featured-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #e03426;
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 6px 12px rgba(224, 52, 38, 0.35);
}

.home-featured-body {
  padding: 16px;
  flex: 1;
}

.home-featured-category {
  color: #e03426;
  font-size: 12px;
  font-weight: 600;
}

.home-featured-title {
  margin: 8px 0 6px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
}

.home-featured-summary {
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  line-height: 1.6;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.home-featured-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.home-articles-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.home-article-card {
  background: rgba(18, 18, 18, 0.95);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.home-article-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.home-article-card:hover {
  transform: translateY(-4px);
  border-color: rgba(224, 52, 38, 0.3);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.35);
}

.home-article-image {
  position: relative;
  height: 150px;
  overflow: hidden;
}

.home-article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.home-article-category {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(224, 52, 38, 0.85);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
}

.home-article-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(224, 52, 38, 0.95);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(224, 52, 38, 0.35);
}

.home-article-body {
  padding: 16px;
}

.home-article-title {
  margin: 0 0 8px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.4;
}

.home-article-summary {
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  line-height: 1.6;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.home-article-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 768px) {
  .home-articles-section {
    padding: 24px 0 16px;
  }

  .home-articles-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .home-featured-card {
    flex-direction: column;
  }

  .home-featured-image {
    width: 100%;
    height: 180px;
  }

  .home-article-image {
    height: 170px;
  }
}

@media (max-width: 1024px) {
  .home-featured-articles,
  .home-articles-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* 知识库式首页视觉优化 */
.home {
  background:
    linear-gradient(90deg, rgba(224, 52, 38, 0.06) 0 1px, transparent 1px),
    linear-gradient(180deg, rgba(224, 52, 38, 0.04) 0 1px, transparent 1px),
    #f6f4ee;
  background-size: 56px 56px;
  color: #1c1b18;
}

.home-knowledge-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr) 300px;
  gap: 0;
  max-width: 1560px;
  margin: 0 auto;
  border-left: 1px solid #dad5c9;
  border-right: 1px solid #dad5c9;
  background:
    linear-gradient(90deg, #faf8f0 0 260px, rgba(255, 253, 246, 0.78) 260px calc(100% - 300px), #faf8f0 calc(100% - 300px));
}

@media (min-width: 961px) {
  .home-knowledge-layout {
    height: calc(100vh - 60px);
    max-height: calc(100vh - 60px);
    overflow: hidden;
    align-items: stretch;
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .knowledge-sidebar {
    grid-row: 1;
    grid-column: 1;
  }

  .knowledge-main {
    grid-row: 1;
    grid-column: 2;
  }

  .knowledge-reference-panel {
    grid-row: 1;
    grid-column: 3;
  }

  .knowledge-site-footer {
    grid-row: 2;
    grid-column: 1 / -1;
  }
}

.knowledge-sidebar,
.knowledge-reference-panel {
  position: relative;
  align-self: stretch;
  padding: 28px 20px;
  background: #faf8f0;
  overflow: hidden;
}

@media (min-width: 961px) {
  .knowledge-sidebar,
  .knowledge-reference-panel {
    height: 100%;
    min-height: 0;
  }
}

.knowledge-sidebar {
  border-right: 1px solid #d9d3c6;
}

.knowledge-reference-panel {
  border-left: 1px solid #d9d3c6;
}

.knowledge-site-footer {
  min-height: 28px;
  padding: 0 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    linear-gradient(90deg, rgba(224, 52, 38, 0.04) 0 1px, transparent 1px),
    linear-gradient(180deg, rgba(224, 52, 38, 0.04) 0 1px, transparent 1px),
    #f6f4ee;
  background-size: 56px 56px;
  border-top: 1px solid #dad5c9;
}

.knowledge-site-footer p {
  margin: 0;
  color: #8a8375;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.08em;
}

.knowledge-brand-mark {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-bottom: 24px;
  margin-bottom: 22px;
  border-bottom: 1px solid #2a2722;
}

.brand-dot {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  background: #e03426;
  border-radius: 50%;
  box-shadow: 24px 0 0 #1f1c18;
}

.knowledge-brand-mark p,
.knowledge-side-note span,
.reference-label,
.knowledge-breadcrumb {
  margin: 0;
  color: #e03426;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.knowledge-brand-mark p {
  line-height: 22px;
  padding-left: 18px;
}

.knowledge-brand-mark strong {
  display: block;
  margin-top: 7px;
  color: #171511;
  font-size: 18px;
  line-height: 1.35;
}

.knowledge-nav {
  display: grid;
  gap: 4px;
}

.knowledge-nav-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 8px;
  color: #3d3932;
  text-decoration: none;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

.knowledge-nav-link:hover,
.knowledge-nav-link.active {
  color: #e03426;
  background: #eeece3;
  border-left-color: #e03426;
}

.knowledge-nav-link em {
  color: #9a9487;
  font-size: 11px;
  font-style: normal;
}

.knowledge-side-note {
  margin-top: 28px;
  padding: 16px;
  background: #fffdf6;
  border: 1px solid #ddd6c9;
}

.knowledge-side-note p {
  margin: 8px 0 0;
  color: #625d52;
  font-size: 13px;
  line-height: 1.7;
}

.knowledge-main {
  min-width: 0;
  padding: 28px 46px 36px;
}

@media (min-width: 961px) {
  .knowledge-main {
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
}

.knowledge-breadcrumb {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  color: #9c9588;
}

.knowledge-breadcrumb i {
  color: #c7c0b2;
  font-style: normal;
}

.fullscreen-carousel.knowledge-hero-card {
  height: clamp(300px, 32vw, 380px);
  margin: 0;
  overflow: hidden;
  background: #171511;
  border: 1px solid #29251f;
  border-radius: 0;
  box-shadow: none;
  user-select: none;
  -webkit-user-select: none;
  touch-action: pan-y;
}

.knowledge-hero-card .carousel-slide-panel {
  background: #171511;
}

.knowledge-hero-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  height: 100%;
  padding: 32px 40px;
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.48) 38%, rgba(0, 0, 0, 0.08) 72%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.06) 58%);
}

.knowledge-hero-copy {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
  max-width: 640px;
  padding: 0;
  border-right: none;
}

.knowledge-hero-visual {
  position: absolute;
  inset: 0;
  z-index: 1;
  min-width: 0;
  padding: 0;
  background: #171511;
}

.knowledge-hero-visual .slide-image-container {
  width: 100%;
  height: 100%;
  border: none;
  background: #171511;
  pointer-events: none;
}

.knowledge-hero-card .slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: saturate(0.96) contrast(1.04);
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

.knowledge-hero-card .slide-title {
  max-width: 520px;
  margin: 8px 0 10px;
  color: #fffdf8;
  font-size: clamp(26px, 2.8vw, 40px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.04em;
  text-shadow: 0 12px 28px rgba(0, 0, 0, 0.48);
}

.knowledge-hero-card .slide-brand {
  margin: 0 0 12px;
  color: rgba(255, 253, 248, 0.74);
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 14px;
  letter-spacing: 0.16em;
}

.knowledge-hero-summary {
  max-width: 480px;
  margin: 0 0 14px;
  padding-left: 14px;
  color: rgba(255, 253, 248, 0.86);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.65;
  border-left: 4px solid #e03426;
}

.knowledge-hero-card .content-type-badge {
  display: inline-flex;
  width: fit-content !important;
  margin: 0 !important;
  padding: 5px 10px;
  color: #fffdf8;
  font-size: 12px;
  letter-spacing: 0.12em;
  border: 1px solid rgba(255, 253, 248, 0.34);
  border-radius: 0;
  background: rgba(224, 52, 38, 0.82);
  box-shadow: none;
  float: none !important;
}

.knowledge-hero-card .view-details-btn {
  width: fit-content;
  padding: 10px 16px;
  color: #fffdf8;
  background: rgba(224, 52, 38, 0.9);
  border: 1px solid rgba(255, 253, 248, 0.78);
  border-radius: 0;
  box-shadow: 4px 4px 0 rgba(0, 0, 0, 0.8);
  backdrop-filter: none;
}

.knowledge-hero-card .view-details-btn:hover {
  color: #fff;
  background: #f04838;
  border-color: #f04838;
  transform: translate(-1px, -1px);
}

.knowledge-hero-card .carousel-indicators {
  bottom: 22px;
}

.knowledge-hero-card .indicator {
  width: 34px;
  height: 3px;
  border-radius: 0;
  background: rgba(255, 253, 248, 0.36);
}

.knowledge-hero-card .indicator.active {
  background: #e03426;
  transform: none;
}

.knowledge-hero-card .carousel-nav {
  width: 36px;
  height: 36px;
  color: #fffdf8;
  background: rgba(0, 0, 0, 0.38);
  border: 1px solid rgba(255, 253, 248, 0.48);
  border-radius: 0;
  backdrop-filter: none;
}

.knowledge-stat-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 18px;
  border: 1px solid #d9d3c6;
  background: #fffdf8;
}

.knowledge-stat-card {
  min-width: 0;
  padding: 18px 20px;
  border-right: 1px solid #d9d3c6;
}

.knowledge-stat-card:last-child {
  border-right: none;
}

.knowledge-stat-card span {
  display: block;
  color: #e03426;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.knowledge-stat-card strong {
  display: block;
  margin-top: 8px;
  color: #171511;
  font-size: 32px;
  line-height: 1;
}

.knowledge-stat-card p {
  margin: 8px 0 0;
  color: #716a5f;
  font-size: 13px;
}

.reference-block {
  padding: 20px 0;
  border-bottom: 1px solid #e2dbce;
}

.reference-block:first-child {
  padding-top: 0;
}

.reference-link,
.reference-empty {
  display: block;
  margin-top: 10px;
  padding-left: 12px;
  color: #5e584e;
  font-size: 13px;
  line-height: 1.65;
  text-decoration: none;
  border-left: 3px solid #d8d1c2;
}

.reference-link:hover {
  color: #e03426;
  border-left-color: #e03426;
}

.home-articles-section,
.brand-section,
.models-display-section {
  max-width: none;
  margin: 28px 0 0;
  background: rgba(255, 253, 246, 0.9);
  border: 1px solid #dad5c9;
}

.home-articles-section {
  padding: 28px 0 30px;
}

.brand-section,
.models-display-section {
  padding-top: 28px;
}

.knowledge-section-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: end;
  margin-bottom: 22px;
  padding: 0 0 18px;
  border-bottom: 1px solid #29251f;
}

.knowledge-section-heading.compact {
  margin-bottom: 16px;
}

.knowledge-section-heading span {
  color: #e03426;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.knowledge-section-heading h2 {
  margin: 0;
  color: #15130f;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.knowledge-section-heading p {
  margin: 6px 0 0;
  color: #70695e;
  font-size: 14px;
  line-height: 1.6;
}

.knowledge-section-heading a {
  color: #171511;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid currentColor;
}

.knowledge-section-heading a:hover {
  color: #e03426;
}

.home-articles-header {
  display: none;
}

.home-articles-title h2,
.brand-section-title,
.model-display-name {
  color: #1a1713;
}

.home-articles-title p,
.brand-count,
.toggle-icon,
.loading-indicator,
.no-more-data {
  color: #80786b;
}

.home-article-card {
  background: #fffdf8;
  border-color: #dcd5c7;
  border-radius: 0;
  box-shadow: 0 12px 34px rgba(39, 35, 29, 0.08);
}

.home-article-card:hover {
  border-color: #e03426;
  box-shadow: 0 18px 40px rgba(39, 35, 29, 0.14);
}

.home-article-title {
  color: #1b1814;
  font-size: 16px;
}

.home-article-summary,
.home-article-meta,
.home-articles-loading,
.home-articles-empty {
  color: #70695e;
}

.brand-section {
  margin-top: 0;
  margin-bottom: 0;
}

.brand-section-header {
  background: #f0ede4;
  margin-bottom: 0;
  border-bottom: 1px solid #d9d3c6;
  border-top: 1px solid #d9d3c6;
}

.brand-section-header:hover {
  background: #ebe7dd;
}

.category-tab,
.alphabet-btn,
.filter-btn {
  color: #635c51;
  border-color: #d2cabc;
}

.category-tab:hover,
.filter-btn:hover,
.alphabet-btn:hover {
  color: #171511;
  border-color: #948a7b;
}

.category-tab.active,
.home-articles-more {
  color: #e03426;
}

.alphabet-btn.active,
.filter-btn.active {
  background: #e03426;
  border-color: #e03426;
  box-shadow: none;
}

.brand-card,
.model-display-card {
  background: #fffdf8;
  border-color: #dcd5c7;
  border-radius: 0;
  box-shadow: none;
}

.brand-card .brand-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.brand-card .brand-logo-img {
  display: block;
  width: 80%;
  height: 80%;
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
  object-position: center;
}

.brand-card:hover,
.model-display-card:hover {
  border-color: #e03426;
  box-shadow: 0 16px 34px rgba(39, 35, 29, 0.14);
}

.model-display-info {
  background: #fffdf8;
  border-top: 1px solid #e0d8ca;
}

.models-content {
  padding: 18px 18px 22px;
}

.models-content .models-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  padding: 0;
}

.model-display-card {
  display: flex;
  flex-direction: column;
  aspect-ratio: auto;
  min-height: 0;
}

.model-display-image {
  height: auto;
  aspect-ratio: 16 / 10;
  border-radius: 0;
  background: #f1eee6;
  padding: 0;
  overflow: hidden;
}

.model-display-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: none;
  will-change: auto;
}

.model-display-card:hover .model-display-img {
  transform: none;
}

.model-display-info {
  min-height: 58px;
  height: auto;
  padding: 12px 14px;
}

.model-display-name {
  color: #1a1713;
  font-size: 14px;
  line-height: 1.35;
}

@media (min-width: 1281px) {
  .models-content .models-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 961px) and (max-width: 1280px) {
  .models-content .models-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .models-content .models-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
    gap: 14px !important;
    padding: 0 !important;
  }

  .model-display-image {
    height: auto !important;
    aspect-ratio: 16 / 10;
  }

  .model-display-info {
    height: auto !important;
  }
}

.filter-divider {
  background: #d2cabc;
}

.brand-content-wrapper {
  padding-bottom: 24px;
}

.brand-category-tabs,
.alphabet-filter,
.filter-control-bar {
  background: #fffdf8;
  border: 1px solid #d9d3c6;
}

.brand-category-tabs {
  border-bottom: none;
}

.alphabet-filter {
  margin-bottom: 16px;
}

.filter-control-bar {
  justify-content: flex-start;
  padding: 14px;
}

.brands-display,
.models-content {
  background: rgba(255, 253, 248, 0.62);
  border: 1px solid #d9d3c6;
}

.models-content {
  border-top: none;
}

@media (max-width: 1280px) {
  .home-knowledge-layout {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  .knowledge-reference-panel {
    display: none;
  }
}

@media (max-width: 960px) {
  .home-knowledge-layout {
    display: block;
    min-height: 0;
    border-right: none;
    border-left: none;
  }

  .knowledge-sidebar {
    position: static;
    min-height: 0;
    padding: 18px 16px;
    border-right: none;
    border-bottom: 1px solid #d9d3c6;
  }

  .knowledge-brand-mark {
    margin-bottom: 14px;
    padding-bottom: 14px;
  }

  .knowledge-nav {
    display: flex;
    overflow-x: auto;
    gap: 8px;
  }

  .knowledge-nav-link {
    flex: 0 0 auto;
    gap: 16px;
    border-left: none;
    border-bottom: 3px solid transparent;
  }

  .knowledge-nav-link:hover,
  .knowledge-nav-link.active {
    border-left-color: transparent;
    border-bottom-color: #e03426;
  }

  .knowledge-side-note {
    display: none;
  }

  .knowledge-main {
    padding: 20px 16px 28px;
  }

  .fullscreen-carousel.knowledge-hero-card {
    height: clamp(260px, 48vw, 340px);
    min-height: 0;
  }

  .knowledge-hero-content {
    padding: 24px 20px;
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.18) 74%),
      linear-gradient(90deg, rgba(0, 0, 0, 0.52) 0%, rgba(0, 0, 0, 0.12) 100%);
  }

  .knowledge-hero-card .slide-title {
    font-size: clamp(22px, 6vw, 32px);
  }

  .knowledge-hero-summary {
    font-size: 13px;
    margin-bottom: 12px;
  }

  .knowledge-stat-strip {
    grid-template-columns: 1fr;
  }

  .knowledge-stat-card {
    border-right: none;
    border-bottom: 1px solid #d9d3c6;
  }

  .knowledge-stat-card:last-child {
    border-bottom: none;
  }

  .knowledge-section-heading {
    grid-template-columns: 1fr;
    gap: 8px;
    align-items: start;
  }

  .knowledge-section-heading h2 {
    font-size: 28px;
  }

  .knowledge-hero-content {
    grid-template-columns: 1fr;
  }

  .knowledge-hero-copy {
    min-height: 0;
    padding: 0;
    border-right: none;
    border-bottom: none;
  }

  .knowledge-hero-visual {
    min-height: 0;
    padding: 0;
  }
}

@media (max-width: 768px) {
  .home-articles-grid {
    grid-template-columns: 1fr;
  }

  .knowledge-hero-card .slide-title {
    font-size: clamp(20px, 7vw, 28px);
  }

  .knowledge-hero-summary {
    font-size: 14px;
  }

  .knowledge-hero-card .carousel-nav {
    display: none;
  }

  .home-articles-section,
  .brand-section,
  .models-display-section {
    border-right: none;
    border-left: none;
  }
}

/* 品牌索引最终覆盖：卡片本身就是 logo 方格，logo 在方格正中心。 */
.brands-grid {
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 10px;
  align-items: stretch;
}

.brands-grid .brand-card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: none;
  aspect-ratio: 1 / 1;
  min-height: 0;
  padding: 0;
  background: #fffdf8;
  border: 1px solid #d9d3c6;
  border-radius: 0;
  overflow: hidden;
}

.brands-grid .brand-card .brand-logo {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 10px;
  background: transparent;
  border-radius: 0;
  overflow: hidden;
}

.brands-grid .brand-card .brand-logo::before {
  display: none;
}

.brands-grid .brand-card .brand-logo-img {
  display: block;
  width: 72%;
  height: 72%;
  max-width: 72%;
  max-height: 72%;
  margin: 0;
  object-fit: contain;
  object-position: center center;
}

.brands-grid .brand-card .no-logo {
  inset: 10px;
}

.brands-grid .brand-card .brand-name {
  display: none;
}

/* 首页三段内容区最终对齐：最新资讯、品牌索引、车型图谱边框相连且全部无圆角。 */
.home-articles-section,
.brand-section,
.models-display-section {
  margin: 0 !important;
  padding: 0 !important;
  background: rgba(255, 253, 248, 0.62) !important;
  border: 1px solid #d9d3c6 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.brand-section,
.models-display-section {
  border-top: none !important;
}

.home-articles-section .content-container,
.brand-section .content-container,
.models-display-section .content-container {
  padding: 0 !important;
}

.knowledge-section-heading,
.knowledge-section-heading.compact {
  margin: 0 !important;
  padding: 24px 28px 18px !important;
  background: rgba(255, 253, 248, 0.92) !important;
  border: none !important;
  border-bottom: 1px solid #29251f !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.home-articles-content,
.home-articles-footer,
.brand-section-header,
.brand-category-tabs,
.alphabet-filter,
.filter-control-bar,
.brands-display,
.models-content {
  border-radius: 0 !important;
  box-shadow: none !important;
}

.home-articles-content {
  padding: 22px 24px 24px !important;
}

.home-articles-footer {
  padding: 0 24px 24px !important;
}

.brand-section-header {
  margin: 0 !important;
  padding: 16px 24px !important;
  background: rgba(255, 253, 248, 0.72) !important;
  border: none !important;
  border-bottom: 1px solid #d9d3c6 !important;
}

.brand-content-wrapper {
  padding: 0 !important;
}

.brand-category-tabs,
.alphabet-filter,
.filter-control-bar {
  margin: 0 !important;
  border-right: none !important;
  border-left: none !important;
}

.brand-category-tabs,
.filter-control-bar {
  border-top: none !important;
  border-bottom: 1px solid #d9d3c6 !important;
}

.alphabet-filter {
  border-top: none !important;
  border-bottom: 1px solid #d9d3c6 !important;
}

.brands-display,
.models-content {
  margin: 0 !important;
  background: transparent !important;
  border: none !important;
}

.brands-display {
  padding: 18px 18px 22px !important;
}

.models-content {
  padding: 18px 18px 22px !important;
}

.home-article-card,
.home-featured-card,
.brand-card,
.model-display-card,
.model-display-image,
.model-display-info,
.model-display-img,
.model-display-placeholder,
.model-image-skeleton {
  border-radius: 0 !important;
}

@media (max-width: 768px) {
  .home-articles-section,
  .brand-section,
  .models-display-section {
    border-right: none !important;
    border-left: none !important;
  }

  .knowledge-section-heading,
  .knowledge-section-heading.compact {
    padding: 20px 16px 16px !important;
  }
}
</style> 
